import { Fragment, useEffect, useState, useMemo, useCallback, useRef } from "react";
import { tr } from "@/i18n";
import { createPortal } from "react-dom";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { Plus, Trash2, ArrowLeft, ChevronDown, ChevronRight, ArchiveRestore, X, UserPlus, Info, Upload } from "lucide-react";
import { getProposals, createProposal, updateProposal, deleteProposal, hardDeleteProposal, getClients, getSites, getMasterScopes, getMasterEquipment, getMasterParts, createMasterPart, createClient, convertProposalToWO, lookupTaxRate, lookupZip, bulkUpdateProposalsPipeline, getAccountManagers, uploadExecutedProposal, getExecutedProposalDocument, generateProposalReviewToken, getSettings } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { setHideTax } from "@/lib/api";
import { CategoryFilter, matchesCategories, useStickyCategories } from "@/components/common/CategoryFilter";
import EmailHistoryPanel from "@/components/EmailHistoryPanel";
import { ExecutedDocDropZone } from "@/components/ExecutedDocDropZone";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Extracted components
import { statusOptions, formatCurrency, DEFAULT_EXCLUSIONS, DEFAULT_TERMS, PROJECT_TYPES, TM_QUALIFICATION, newLineItem, newScope, partToLineItem, calcProposalTotals, setDefaultRates, withDefaultMargins } from "@/components/proposals/proposalUtils";
import { wcConfig, wcLineItems } from "@/components/proposals/windowCare";
import { ProposalPreview } from "@/components/proposals/ProposalPreview";
import { PipelineCard, PROPOSAL_STATUS_COLORS } from "@/components/proposals/PipelineCard";
import PillSelect from "@/components/common/PillSelect";
import { ProposalContactPicker } from "@/components/proposals/ProposalContactPicker";
import { ProposalEmailDialog } from "@/components/proposals/ProposalEmailDialog";
import { CustomerNotesPanel } from "@/components/CustomerNotesPanel";
import { FollowupActivityLog } from "@/components/proposals/FollowupActivityLog";
import { ProposalBuilderActionBar } from "@/components/proposals/ProposalBuilderActionBar";
import { useCanOverrideTax } from "@/components/common/TaxOverrideDialog";
import SearchBar from "@/components/common/SearchBar";
import { PillRail, Pill, PillDivider } from "@/components/common/Segmented";
import StickyTop from "@/components/common/StickyTop";

import { useEmailDocMode, docFilename, withDocName } from "@/components/common/EmailDocMode";
import { downloadPdfUrl } from "@/lib/downloadBlob";
import { SortableHeader, HeaderFilter } from "@/components/common/SortHeader";
import { btn, BTN } from "@/components/common/btn";
import { shortDate, money } from "@/lib/format";
import { activePeople, assignablePeople } from "@/lib/roster";
import { EstimateGrid } from "@/components/proposals/grid/EstimateGrid";
import { consolidateOtherCosts } from "@/components/proposals/rail/OtherCostsCard";
import { AddScopesButton } from "@/components/proposals/AddScopesButton";
import { ImportQuotesDialog } from "@/components/proposals/grid/ImportQuotesDialog";
import { ProposalRail } from "@/components/proposals/rail/ProposalRail";
import { DEFAULT_COLS } from "@/components/proposals/grid/gridMath";
import { findLibraryPart, applyLibraryValues, acceptLibraryBaseline, REVIEWABLE_STATUSES } from "@/components/proposals/grid/libraryDiff";
import { useUnsavedGuard } from "@/components/proposals/useUnsavedGuard";

// ============== ARCHIVED (WON / LOST) SECTION ==============
// iter243 — split the single "Archived" list into two fast-access
// sections: Won and Lost. Restore-to-draft only offered on Lost rows
// (the server keeps Won proposals won).
// UI sweep: these are now the "Won" / "Lost" views picked from the toolbar pills, so the section
// renders only the table (the pill is the toggle).
function ArchivedProposalsSection({ label = "Archived", statusKey = "archived", items, getClientName, onEdit, onRestore, onHardDelete, canHardDelete }) {
  const isWon = (p) => p.status === "won";
  const [sort, setSort] = useState({ field: "date", dir: "desc" });
  const dateOf = (p) => isWon(p) ? (p.won_at || p.signed_at || p.updated_at || p.created_at) : (p.lost_at || p.updated_at || p.created_at);
  const sorted = useMemo(() => {
    const val = (p) => {
      switch (sort.field) {
        case "name": return (p.name || "").toLowerCase();
        case "customer": return (getClientName(p.client_id) || "").toLowerCase();
        case "am": return (p.prepared_by || "").toLowerCase();
        case "value": return p.value || 0;
        default: return dateOf(p) || "";
      }
    };
    return [...items].sort((a, b) => { const x = val(a), y = val(b); return x < y ? (sort.dir === "asc" ? -1 : 1) : x > y ? (sort.dir === "asc" ? 1 : -1) : 0; });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, sort, getClientName]);
  const clickSort = (field) => setSort(s => s.field === field ? { field, dir: s.dir === "asc" ? "desc" : "asc" } : { field, dir: "asc" });
  const Th = ({ field, children, className = "" }) => (
    <TableHead className={`text-xs ${className}`}>
      <SortableHeader label={children} sorted={sort.field === field} direction={sort.dir} onSort={() => clickSort(field)} testId={`${statusKey}-sort-${field}`} />
    </TableHead>
  );
  return (
    <div data-testid={`${statusKey}-proposals-group`}>
      {sorted.length === 0 ? (
        <div className="text-center py-10 text-slate-500 text-sm">{tr("No {what} proposals", { what: tr(label).toLowerCase() })}</div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <Th field="name" className="min-w-[220px]">{tr("Proposal")}</Th>
              <Th field="customer" className="min-w-[160px]">{tr("Customer")}</Th>
              <Th field="am" className="w-[80px]">AM</Th>
              <Th field="value" className="w-[110px] num">{tr("Total Sales")}</Th>
              <Th field="date" className="w-[90px] num">{tr("Closed")}</Th>
              <TableHead className="w-[70px] text-xs">{tr("Status")}</TableHead>
              <TableHead className="w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map(proposal => {
              const dateStr = dateOf(proposal);
              const won = isWon(proposal);
              return (
                <TableRow key={proposal.id} className="hover:bg-slate-50" data-testid={`archived-proposal-${proposal.id}`}>
                  <TableCell className="max-w-[320px]"><button type="button" className="block w-full truncate text-left text-xs font-medium text-brand hover:underline" onClick={() => onEdit(proposal)}>{proposal.name}</button></TableCell>
                  <TableCell className="text-xs text-slate-600">{getClientName(proposal.client_id)}</TableCell>
                  <TableCell className="text-xs text-slate-600">{proposal.prepared_by ? proposal.prepared_by.split(" ")[0] : <span className="text-slate-300">—</span>}</TableCell>
                  <TableCell className="text-xs font-medium num">{money(proposal.value)}</TableCell>
                  <TableCell className="text-xs text-slate-500 num">{shortDate(dateStr)}</TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${won ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`} data-testid={`archived-proposal-status-${proposal.id}`}>{won ? "Won" : "Lost"}</span>
                  </TableCell>
                  <TableCell>
                    {won ? (
                      <div className="flex items-center gap-2">
                        {proposal.converted_wo_id ? (
                          <span className="text-[11px] font-mono text-brand">{proposal.converted_wo_number || "WO linked"}</span>
                        ) : (
                          <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium bg-emerald-100 text-emerald-700 border-emerald-200 whitespace-nowrap">{tr("No WO yet")}</span>
                        )}
                        {canHardDelete && !proposal.converted_wo_id && (
                          <button
                            type="button"
                            title="Permanently delete this duplicate (asks you to confirm)"
                            className="p-1 text-slate-400 hover:text-red-600 rounded"
                            onClick={(e) => { e.stopPropagation(); onHardDelete(proposal); }}
                            data-testid={`hard-delete-proposal-${proposal.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    ) : (
                      <button type="button" className={btn("mode")} title="Put this proposal back in the open list as a draft"
                        onClick={() => onRestore(proposal)} data-testid={`restore-proposal-${proposal.id}`}>
                        <ArchiveRestore className="w-3.5 h-3.5" /> {tr("Restore")}
                      </button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// Column header: name sorts, click again flips; arrow only on the ruling column. Module-level so the header
// (and any filter popover inside it) does not remount on every page render. Same <th> chrome as the Work Orders list.
const Th = ({ field, children, filter, testId, className = "", sortField, sortDirection, onSort }) => (
  <TableHead className={`whitespace-nowrap text-xs ${className}`}>
    <SortableHeader label={children} sorted={sortField === field} direction={sortDirection} onSort={() => onSort(field)} filter={filter} testId={testId} />
  </TableHead>
);

// Status sorts by workflow order, like the Work Orders list.
const PROPOSAL_STATUS_ORDER = statusOptions.map((o) => o.value);
const gmOf = (p) => {
  if (!Array.isArray(p.scopes) || !p.scopes.length) return null;
  try { const t = calcProposalTotals(p.scopes, 0, 0); return t.subtotal - (t.costMaterial + t.costFreight + t.costLabor + t.costEquipment + t.costTravel + t.costDisposal); } catch { return null; }
};

// Open (default) / Won / Lost — the toolbar view pills. Remembered per user (localStorage) so a Won
// review survives a refresh; Open is the default list and has no pill.
function useProposalView(userId) {
  const key = `abs.proposals.view.${userId || "anon"}`;
  const [view, setView] = useState(() => {
    try { const v = localStorage.getItem(key); return ["open", "archived"].includes(v) ? v : "open"; } catch { return "open"; }
  });
  useEffect(() => { try { localStorage.setItem(key, view); } catch {} }, [key, view]);
  return [view, setView];
}

// ============== PROPOSAL BUILDER COMPONENT ==============
function ProposalBuilder({ proposal, clients, onSave, onClose, masterScopes, masterEquipment, masterParts, prefill, onClientCreated, onConvertToWO, returnToWO, onReturnToWO, accountManagers = [] }) {
  const [form, setForm] = useState({
    name: "",
    client_id: "",
    site_id: "",
    status: "draft",
    description: "",
    notes: "",
    scopes: [],
    qualifications: [
      "Proposal limited to listed scope only; all additional labor, materials, or services are extra at $165.00/man-hour and subject to scheduling and approval",
      "Pricing assumes normal business hours; all after-hours or weekend work billed at premium rates",
    ],
    tax_rate: 0,
    tax_rate_manual: false,
    cc_fee_pct: 0,
    hide_tax: false,
    // iter257 — proposal-level discount ("pct" of subtotal or "flat" $).
    discount_type: "flat",
    discount_value: 0,
    default_margins: null,
    show_breakdown: false,
    tax_breakdown: null,
    tax_authority: "",
    prepared_by: (typeof window !== "undefined" && (window.__ABS_USER_NAME || localStorage.getItem("abs_current_user") || localStorage.getItem("abs_prepared_by"))) || "",
    prepared_by_email: localStorage.getItem("abs_current_user_email") || localStorage.getItem("abs_prepared_by_email") || "",
    exclusions: [...DEFAULT_EXCLUSIONS],
    terms: [...DEFAULT_TERMS],
    proposal_date: new Date().toISOString().split("T")[0],
    customer_po_number: "",
    project_address: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    // iter210 — start empty so the "already converted → open WO"
    // ternary in ProposalBuilderActionBar never reads undefined.
    converted_wo_id: "",
    converted_wo_number: "",
  });
  const [sites, setSites] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expandedScopes, setExpandedScopes] = useState({});
  const gridCols = DEFAULT_COLS;
  const [showPreview, setShowPreview] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState("");
  const [dirty, setDirty] = useState(false);
  const discardedRef = useRef(false);
  const discardEdits = () => {
    discardedRef.current = true;
    if (autoSaveTimer.current) { clearTimeout(autoSaveTimer.current); autoSaveTimer.current = null; }
    autoSaveSeqRef.current++;
    setDirty(false);
  };
  const autoSaveTimer = useRef(null);
  const isFirstLoad = useRef(true);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [newCustomerData, setNewCustomerData] = useState({ name: "", contact_name: "", contact_email: "", contact_phone: "", address: "", city: "", state: "", zip_code: "" });
  const [customerSearch, setCustomerSearch] = useState("");
  const [clientList, setClientList] = useState(clients);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [showImportQuotes, setShowImportQuotes] = useState(false);
  const [includeSignLink, setIncludeSignLink] = useState(true);
  const [docMode, setDocMode] = useEmailDocMode(); // link vs download & attach (sticky, app-wide)
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [selectedContactEmail, setSelectedContactEmail] = useState("");
  const [selectedContactId, setSelectedContactId] = useState("");  // #155 (Group G) — stable id for SelectItem value
  const [showAddContactInline, setShowAddContactInline] = useState(false);
  const [addressOverridden, setAddressOverridden] = useState(false);
  const [titleSlot, setTitleSlot] = useState(null);
  useEffect(() => { setTitleSlot(document.getElementById("proposal-title-right")); }, []);
  // iter262 — drop-signed-PDF Foundation-choice modal.
  const [signedPdfPending, setSignedPdfPending] = useState(null);  // { file, resolve, reject }
  const [signedPdfSyncMode, setSignedPdfSyncMode] = useState("skipped");

  // iter250 — capture the proposal id in a ref so unmount / cross-render
  // flushes can pass it explicitly to the parent's handleSave. Without
  // this, an async .then() flush could fire AFTER the parent had already
  // cleared editingProposal → parent's ref reads null → handleSave takes
  // the CREATE branch and produces a DUPLICATE of the just-viewed
  // proposal (user reported opening a Won proposal and having it clone
  // itself in the Won list on close).
  const proposalIdRef = useRef(proposal?.id || null);
  useEffect(() => { proposalIdRef.current = proposal?.id || null; }, [proposal?.id]);
  const formRef = useRef(form);
  const onSaveRef = useRef(onSave);
  useEffect(() => { formRef.current = form; }, [form]);
  useEffect(() => { onSaveRef.current = onSave; }, [onSave]);

  useEffect(() => { setClientList(clients); }, [clients]);

  // Escape key handler
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        if (showPreview) { setShowPreview(false); e.preventDefault(); }
        else if (showEmailDialog) { setShowEmailDialog(false); e.preventDefault(); }
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [showPreview, showEmailDialog]);

  // Whenever the email dialog opens, default the recipient to the proposal's saved contact
  useEffect(() => {
    if (showEmailDialog && form.contact_email && !selectedContactEmail) {
      setSelectedContactEmail(form.contact_email);
    }
  }, [showEmailDialog, form.contact_email, selectedContactEmail]);
  const isAutosaving = useRef(false);
  // Hydrate on open and when switching to a different saved proposal. Autosave responses (same id, or
  // new → first id) never reset the form: while editing, the form is the source of truth, and re-hydrating
  // after every save re-ran the tax lookup → form change → autosave → … forever.
  const hydratedRef = useRef(undefined);
  useEffect(() => {
    const id = proposal?.id || null;
    if (hydratedRef.current !== undefined && (hydratedRef.current === id || hydratedRef.current === null)) { hydratedRef.current = id; return; }
    hydratedRef.current = id;
    isFirstLoad.current = true;
    prevTaxKeyRef.current = null;
    if (proposal) {
      setForm({
        name: proposal.name || "",
        client_id: proposal.client_id || "",
        site_id: proposal.site_id || "",
        status: proposal.status || "draft",
        project_type: proposal.project_type || "service",
        description: proposal.description || "",
        notes: proposal.notes || "",
        scopes: consolidateOtherCosts(proposal.scopes || []),
        qualifications: proposal.qualifications?.length ? proposal.qualifications : [""],
        tax_rate: proposal.tax_rate || 0,
        tax_rate_manual: !!proposal.tax_rate_manual,
        tax_override: proposal.tax_override || null,
        cc_fee_pct: proposal.cc_fee_pct == null ? 0 : proposal.cc_fee_pct,
        hide_tax: !!proposal.hide_tax,
        // iter257 — hydrate discount from stored proposal doc.
        discount_type: proposal.discount_type || "flat",
        discount_value: proposal.discount_value == null ? 0 : proposal.discount_value,
        default_margins: proposal.default_margins || null,
        show_breakdown: proposal.show_breakdown || false,
        tax_breakdown: proposal.tax_breakdown || null,
        tax_authority: proposal.tax_authority || "",
        prepared_by: proposal.prepared_by || localStorage.getItem("abs_current_user") || "",
        prepared_by_email: proposal.prepared_by_email || localStorage.getItem("abs_current_user_email") || "",
        exclusions: proposal.exclusions?.length ? proposal.exclusions : [...DEFAULT_EXCLUSIONS],
        terms: proposal.terms?.length ? proposal.terms : [...DEFAULT_TERMS],
        proposal_date: proposal.proposal_date || proposal.created_at?.split("T")[0] || new Date().toISOString().split("T")[0],
        customer_po_number: proposal.customer_po_number || "",
        pipeline_contact_id: proposal.pipeline_contact_id || null,
        project_address: proposal.project_address || "",
        contact_name: proposal.contact_name || "",
        contact_email: proposal.contact_email || "",
        contact_phone: proposal.contact_phone || "",
        // iter210 — carry the denormalized linked-WO reference so the
        // action bar can flip "Already Converted" into an actionable
        // "Work Order S00XXXX" deep-link button.
        converted_wo_id: proposal.converted_wo_id || "",
        converted_wo_number: proposal.converted_wo_number || "",
        // Carry signature fields so Preview can render the customer's acceptance
        signature_data: proposal.signature_data || "",
        signed_by: proposal.signed_by || "",
        signed_at: proposal.signed_at || "",
      });
      if (proposal.client_id) loadSites(proposal.client_id);
      // Expand all scopes
      const expanded = {};
      (proposal.scopes || []).forEach(s => { expanded[s.id] = true; });
      setExpandedScopes(expanded);
    } else {
      setExpandedScopes({});
      setForm(f => ({ ...f, scopes: [] }));
    }
  }, [proposal]);

  // Auto-populate proposal contact from the client's primary contact when missing.
  useEffect(() => {
    if (!form.client_id) return;
    if (form.contact_email) return;  // already set — don't override
    const client = clientList.find(c => c.id === form.client_id);
    const contacts = (client?.contacts || []).filter(c => c.email);
    if (!contacts.length) return;
    const primary = contacts.find(c => c.is_primary) || contacts[0];
    if (primary) {
      setForm(f => ({
        ...f,
        contact_name: primary.name || "",
        contact_email: primary.email || "",
        contact_phone: primary.phone || "",
      }));
    }
  }, [form.client_id, clientList, form.contact_email]);

  // Pre-fill from Customer tab navigation
  useEffect(() => {
    if (!proposal && prefill?.client_id) {
      const client = clientList.find(c => c.id === prefill.client_id);
      setForm(f => ({ ...f, client_id: prefill.client_id, site_id: prefill.site_id || "", hide_tax: !!client?.hide_tax }));
      loadSites(prefill.client_id);
    }
    // Runs only when prefill changes (entry from another tab) — we
    // intentionally do not re-trigger when clientList or proposal flip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  // Seed cc_fee_pct from Settings → Default CC Fee % on every NEW proposal.
  // Existing proposals (the `proposal` prop is set) keep their saved value.
  // Runs once when the form opens for create mode.
  useEffect(() => {
    let cancelled = false;
    getSettings().then(r => {
      if (cancelled) return;
      // Settings → Default Rates seed every NEW line / scope (labor $/hr, labor GM, material GM, freight GM,
      // drive-time GM). Existing items keep their saved values untouched.
      setDefaultRates(r.data || {});
      if (proposal) return;
      const v = parseFloat(r.data?.default_cc_fee_pct);
      if (Number.isFinite(v) && v > 0) {
        setForm(f => ({ ...f, cc_fee_pct: v }));
      }
    }).catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposal?.id]);

  const loadSites = async (clientId) => {
    try {
      const res = await getSites({ client_id: clientId });
      setSites(res.data);
    } catch { setSites([]); }
  };

  // Auto-fill project address from site when sites load and address is empty
  useEffect(() => {
    if (sites.length > 0 && form.site_id && !form.project_address) {
      const site = sites.find(s => s.id === form.site_id);
      if (site) {
        const addr = [site.address, site.city, site.state, site.zip_code].filter(Boolean).join(", ");
        if (addr) setForm(f => ({ ...f, project_address: addr }));
      }
    }
    // We only want to backfill on initial sites/site selection — adding
    // form.project_address to deps would re-trigger after every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sites, form.site_id]);

  // Auto-lookup the jurisdiction tax rate when the site changes.
  // Also fires for NEW customers the moment city + state are typed — so the
  // tax rate + tax authority (`tax_no`) auto-populate BEFORE the customer
  // record is even created.
  const prevTaxKeyRef = useRef(null);
  useEffect(() => {
    // Branch A — existing customer with a selected site
    let city = "";
    let state = "";
    let keyId = "";
    if (isNewCustomer) {
      city = (newCustomerData.city || "").trim();
      state = (newCustomerData.state || "").trim();
      keyId = `new_${city}_${state}`;
    } else {
      if (!form.site_id) return;
      const site = sites.find(s => s.id === form.site_id);
      if (!site || !site.state) return;
      city = site.city || "";
      state = site.state || "";
      keyId = form.site_id;
    }
    if (!state) return;
    const key = `${keyId}_${form.client_id || ""}`;
    if (prevTaxKeyRef.current === key) return;
    prevTaxKeyRef.current = key;
    // #163 (Group G): jurisdiction-change must override stale rate.
    // When the *jurisdiction* (site / city / state / tax-exempt flag)
    // changes — detected via prevTaxKeyRef above — we re-look up the
    // authoritative rate even if the proposal already has a positive
    // tax_rate. The cached key guard above prevents the autosave-induced
    // refetch loop that the old `parseFloat(form.tax_rate) > 0` early
    // return was guarding against; manual overrides for the SAME
    // jurisdiction are still preserved (prevTaxKeyRef matches → return).
    // iter310 — one path: the site jurisdiction rate always applies.
    lookupTaxRate({ city, state, client_id: form.client_id || "" })
      .then(res => setForm(prev => {
        // Sticky manual override: a hand-entered rate survives a
        // jurisdiction change. We still record what the lookup WOULD
        // have returned so the UI can offer "use auto rate".
        const next = {
          tax_rate: prev.tax_rate_manual ? prev.tax_rate : res.data.total_rate,
          tax_rate_auto: res.data.total_rate,
          tax_breakdown: res.data.breakdown,
          tax_authority: res.data.tax_no || prev.tax_authority,
          tax_no: res.data.tax_no || prev.tax_no,
        };
        // Same values → same object, so no form change (and no autosave) is triggered.
        const same = Object.keys(next).every(k => JSON.stringify(prev[k] ?? null) === JSON.stringify(next[k] ?? null));
        return same ? prev : { ...prev, ...next };
      }))
      .catch(() => {});
  }, [form.site_id, form.client_id, sites, isNewCustomer, newCustomerData.city, newCustomerData.state]);

  const getPartsForScopeName = useCallback((scopeName) => {
    if (!scopeName) return [];
    const matchingScope = masterScopes.find(ms => ms.name.toLowerCase() === scopeName.toLowerCase());
    if (!matchingScope) return [];
    return masterParts.filter(p => p.scope_id === matchingScope.id);
  }, [masterScopes, masterParts]);

  const totals = useMemo(() => calcProposalTotals(
    form.scopes,
    form.tax_rate,
    form.cc_fee_pct == null ? 0 : form.cc_fee_pct,
    form.discount_type || "flat",
    form.discount_value == null ? 0 : form.discount_value,
    form.tax_override,
  ), [form.scopes, form.tax_rate, form.cc_fee_pct, form.discount_type, form.discount_value, form.tax_override]);
  // iter306 — manual tax override (dialog + badge). Server enforces roles.
  const canOverrideTax = useCanOverrideTax();
  const [taxOverrideOpen, setTaxOverrideOpen] = useState(false);
  const applyTaxOverrideResult = (doc) => setForm(f => ({
    ...f,
    tax_override: doc?.tax_override || null,
    tax_rate: doc?.tax_rate ?? f.tax_rate,
    tax_rate_manual: !!doc?.tax_rate_manual,
    tax_authority: doc?.tax_authority || f.tax_authority,
  }));

  // Auto-fill part_number from master parts for items missing it
  const backfillDone = useRef(false);
  useEffect(() => { backfillDone.current = false; }, [proposal?.id]);
  // Map part_number → on_hand for stock display in line items
  const partsStockMap = useMemo(() => {
    const m = {};
    masterParts.forEach(p => { if (p.part_number) m[p.part_number] = p.on_hand ?? 0; });
    return m;
  }, [masterParts]);

  useEffect(() => {
    // Only run once per proposal load
    const hasItems = form.scopes.some(s => s.line_items.some(li => li.name));
    if (!hasItems || backfillDone.current) return;
    backfillDone.current = true;
    let changed = false;
    const updatedScopes = form.scopes.map(scope => ({
      ...scope,
      line_items: (scope.line_items || []).map(li => {
        if (li.part_number || !li.name) return li;
        const match = masterParts.find(mp => mp.name?.toLowerCase() === li.name?.toLowerCase());
        if (match?.part_number) {
          changed = true;
          return { ...li, part_number: match.part_number };
        }
        return li;
      })
    }));
    if (changed) setForm(f => ({ ...f, scopes: updatedScopes }));
  }, [masterParts, form.scopes]);

  // iter248 — Serialize ALL saves (debounced autosave + manual) through a
  // single promise chain. Previously the manual Save button called
  // clearTimeout on a pending autosave timer, but that does nothing if
  // the timer had already fired and was awaiting the network response.
  // That race caused:
  //   (a) Duplicate createProposal calls when the user hit Save right
  //       after edits (2× rows in DB, "randomly not saving" symptom).
  //   (b) Stale autosave landing AFTER the manual save and overwriting
  //       the newer server data with earlier form values → user opens
  //       later and their last edits are gone.
  // The chain guarantees at-most-one save in flight; the SEQ token
  // (autoSaveSeqRef) still prevents stale UI status updates.
  const saveChainRef = useRef(Promise.resolve());
  const runSerializedSave = (fn) => {
    const p = saveChainRef.current.then(() => fn(), () => fn()); // never break the chain
    saveChainRef.current = p.catch(() => {});
    return p;
  };

  // Auto-save (debounced, for both new and existing proposals)
  // #78 (M3): each scheduled save gets a monotonically-incrementing
  // sequence token. When a save returns, we check whether a NEWER one
  // has been kicked off since — if so, we discard its result (no
  // status update, no toast, no resetting the form). This prevents a
  // slow stale request from overwriting newer data, and prevents a
  // stale "Save failed" from clobbering a "Saved" that landed first.
  const autoSaveSeqRef = useRef(0);
  useEffect(() => {
    if (isFirstLoad.current) { isFirstLoad.current = false; return; }
    setDirty(true);
    if (!form.name?.trim()) { setAutoSaveStatus("Add a name to start saving"); return; }
    if (!proposal && !form.client_id && !isNewCustomer) { setAutoSaveStatus("Pick a customer to start saving"); return; }
    setAutoSaveStatus("Unsaved changes…");
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      // Clear any previous "Save failed" so the next save starts clean.
      const mySeq = ++autoSaveSeqRef.current;
      runSerializedSave(async () => {
        // Skip this scheduled autosave if a newer one has been enqueued
        // OR a manual save is racing (autoSaveSeqRef gets bumped there too).
        if (mySeq !== autoSaveSeqRef.current) return;
        try {
          isAutosaving.current = true;
          setAutoSaveStatus("Saving...");
          const t = calcProposalTotals(form.scopes, form.tax_rate, form.cc_fee_pct == null ? 0 : form.cc_fee_pct, form.discount_type || "flat", form.discount_value == null ? 0 : form.discount_value, form.tax_override);
          await onSave({ ...form, value: t.total, qualifications: form.qualifications }, true);
          if (mySeq !== autoSaveSeqRef.current) return;
          setDirty(false);
          setAutoSaveStatus("All changes saved");
        } catch {
          if (mySeq !== autoSaveSeqRef.current) return;
          setAutoSaveStatus("Save failed");
        } finally {
          if (mySeq === autoSaveSeqRef.current) isAutosaving.current = false;
        }
      });
    }, 1500);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
    // Debounced autosave fires on form changes only. `onSave` is the
    // parent's callback — including it would re-arm the timer when the
    // parent re-renders and break the debounce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  // iter248 — On builder unmount (user closed the dialog), if a debounced
  // autosave is still pending, flush it synchronously so trailing edits
  // aren't discarded. Without this, keystrokes made in the last 1.5s
  // window before closing were silently thrown away.
  //
  // iter250 — Pass proposalIdRef.current into the payload as
  // _flush_proposal_id so the parent's handleSave can use it EVEN AFTER
  // its own editingProposal state has been cleared (the .then() chain
  // runs asynchronously; by the time it fires, editingProposalRef in the
  // parent has already been nulled out). Fixes the duplicate-on-close
  // bug when reviewing existing Won proposals.
  useEffect(() => {
    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = null;
        if (discardedRef.current) return;
        const f = formRef.current;
        const pid = proposalIdRef.current;
        if (f?.name?.trim()) {
          try {
            const t = calcProposalTotals(f.scopes, f.tax_rate, f.cc_fee_pct == null ? 0 : f.cc_fee_pct, f.discount_type || "flat", f.discount_value == null ? 0 : f.discount_value, f.tax_override);
            saveChainRef.current = saveChainRef.current
              .then(() => onSaveRef.current?.({ ...f, value: t.total, qualifications: f.qualifications, _flush_proposal_id: pid }, true))
              .catch(() => {});
          } catch { /* noop */ }
        }
      }
    };
  }, []);

  // iter249 — flushSave: force any pending debounced autosave to run and
  // await the serialized save chain to drain before returning. Used by
  // Email / View / PDF actions so those flows always reflect the LATEST
  // form state on the server (previously PDFs were rebuilt from stale DB
  // rows when the user clicked Email/PDF within the 1.5s autosave window
  // → wrong totals in customer emails).
  const flushSave = async () => {
    if (autoSaveTimer.current) {
      clearTimeout(autoSaveTimer.current);
      autoSaveTimer.current = null;
    }
    const f = formRef.current;
    if (!f?.name?.trim()) return;
    // Enqueue one final save behind any in-flight ones so we know the
    // server is up-to-date when we return.
    const t = calcProposalTotals(f.scopes, f.tax_rate, f.cc_fee_pct == null ? 0 : f.cc_fee_pct, f.discount_type || "flat", f.discount_value == null ? 0 : f.discount_value, f.tax_override);
    autoSaveSeqRef.current++;
    setAutoSaveStatus("Saving...");
    try {
      await runSerializedSave(async () => {
        await onSaveRef.current?.({ ...f, value: t.total, qualifications: f.qualifications }, true);
      });
      setAutoSaveStatus("Saved");
      setTimeout(() => setAutoSaveStatus(s => s === "Saved" ? "" : s), 1500);
    } catch {
      setAutoSaveStatus("Save failed");
      throw new Error("Save failed — please try again");
    }
  };

  const openPreviewFlush = async () => {
    try { await flushSave(); } catch (e) { toast.error(e.message || "Save failed"); return; }
    setShowPreview(true);
  };
  const openEmailDialogFlush = async () => {
    try { await flushSave(); } catch (e) { toast.error(e.message || "Save failed"); return; }
    setShowEmailDialog(true);
  };

  const updateScope = (scopeId, key, value, extra = null) => {
    setForm(f => ({ ...f, scopes: f.scopes.map(s => s.id === scopeId ? { ...s, [key]: value, ...(extra || {}) } : s) }));
  };

  // T&M template — switching Project Type to T&M offers to strip the
  // proposal down: one $0 "Time & Materials" scope + T&M qualifications.
  const handleProjectTypeChange = (v) => {
    if (v === "tm" && form.project_type !== "tm") {
      const apply = window.confirm(
        "Apply the T&M template? This replaces the current scopes with a single 'Time & Materials' scope and swaps qualifications to standard T&M language."
      );
      if (apply) {
        setForm(f => ({
          ...f,
          project_type: v,
          scopes: [{ ...newScope("Time & Materials"), line_items: [] }],
          qualifications: [TM_QUALIFICATION],
        }));
        setExpandedScopes({});
        return;
      }
    }
    setForm(f => ({ ...f, project_type: v }));
  };

  const addScope = (name = "", libraryParts = []) => {
    const s = newScope(name);
    s.line_items = (libraryParts.length > 0 ? libraryParts.map(p => partToLineItem(p)) : s.line_items).map(li => withDefaultMargins(li, form.default_margins));
    setForm(f => ({ ...f, scopes: [...f.scopes, s] }));
    setExpandedScopes(prev => ({ ...prev, [s.id]: true }));
  };

  const addScopesFromLibrary = (scopeObjs) => {
    const added = scopeObjs.map(ms => {
      const s = newScope(ms.name);
      s.line_items = s.line_items.map(li => withDefaultMargins(li, form.default_margins));
      return s;
    });
    if (!added.length) return;
    setForm(f => ({ ...f, scopes: [...f.scopes, ...added] }));
    setExpandedScopes(prev => ({ ...prev, ...Object.fromEntries(added.map(s => [s.id, true])) }));
  };

  const removeScope = (scopeId) => {
    setForm(f => ({ ...f, scopes: f.scopes.filter(s => s.id !== scopeId) }));
  };

  const addLineItem = (scopeId) => {
    setForm(f => ({
      ...f,
      scopes: f.scopes.map(s => s.id === scopeId ? { ...s, line_items: [...s.line_items, withDefaultMargins(newLineItem(), f.default_margins)] } : s)
    }));
  };

  const addLineItemsFromParts = (scopeId, parts) => {
    if (!parts?.length) return;
    setForm(f => ({
      ...f,
      scopes: f.scopes.map(s => {
        if (s.id !== scopeId) return s;
        const firstEmpty = s.line_items.length === 1 && !s.line_items[0].name;
        const fresh = parts.map(p => withDefaultMargins(partToLineItem(p), f.default_margins));
        return { ...s, line_items: firstEmpty ? fresh : [...s.line_items, ...fresh] };
      })
    }));
    toast.success(`${parts.length} part${parts.length === 1 ? "" : "s"} added`);
  };

  const addLineItemFromPart = (scopeId, part) => {
    setForm(f => ({
      ...f,
      scopes: f.scopes.map(s => {
        if (s.id !== scopeId) return s;
        // If first item is empty, fill it; otherwise append
        const firstEmpty = s.line_items.length === 1 && !s.line_items[0].name;
        const li = withDefaultMargins(partToLineItem(part), f.default_margins);
        return { ...s, line_items: firstEmpty ? [li] : [...s.line_items, li] };
      })
    }));
  };

  const updateLineItem = (scopeId, itemId, key, value) => {
    setForm(f => ({
      ...f,
      scopes: f.scopes.map(s => {
        if (s.id !== scopeId) return s;
        // Window Care pane lines are generated from the estimator — an edit to
        // the price or sub cost on the line goes back into the rate sheet so
        // the panel and the line can never disagree.
        const target = s.line_items.find(li => li.id === itemId);
        if (target?.wc_key && (key === "flat_rate" || key === "subcontract_cost") && s.window_care) {
          const field = key === "flat_rate" ? "price" : "sub_cost";
          const cfg = {
            ...wcConfig(s),
            rates: wcConfig(s).rates.map(r => r.key === target.wc_key ? { ...r, [field]: parseFloat(value) || 0 } : r),
          };
          return { ...s, window_care: cfg, line_items: wcLineItems(cfg) };
        }
        return { ...s, line_items: s.line_items.map(li => li.id === itemId ? { ...li, [key]: value } : li) };
      })
    }));
  };

  // Estimator → scope: config is stored on the scope and the line items are
  // rebuilt from it, so every downstream total/PDF/WO path stays unchanged.
  const applyWindowCare = (scopeId, cfg) => {
    setForm(f => ({
      ...f,
      scopes: f.scopes.map(s => s.id === scopeId ? { ...s, window_care: cfg, line_items: wcLineItems(cfg) } : s)
    }));
  };

  const removeLineItem = (scopeId, itemId) => {
    setForm(f => ({
      ...f,
      scopes: f.scopes.map(s => s.id === scopeId ? {
        ...s,
        line_items: s.line_items.filter(li => li.id !== itemId)
      } : s)
    }));
  };

  // Vendor quote import — matched library parts keep library hours/margins; the quote's unit cost always wins.
  const importedToLines = (items, meta = {}) => items.map((it) => {
    const part = it.part_number && masterParts.find((p) => p.part_number && p.part_number.toLowerCase() === it.part_number.toLowerCase());
    const base = withDefaultMargins(part ? partToLineItem(part) : newLineItem(), form.default_margins);
    return {
      ...base,
      name: it.name || base.name,
      part_number: it.part_number || base.part_number,
      description: it.description || base.description,
      qty: parseInt(it.qty) || 1,
      material_cost: parseFloat(it.unit_cost) || 0,
      manufacturer: base.manufacturer || meta.vendor || "",
    };
  });
  const realLines = (lis) => lis.filter(li => li.name || li.part_number || parseFloat(li.material_cost) > 0 || parseFloat(li.labor_hours) > 0);

  const addImportedLines = (scopeId, items, meta = {}) => {
    const lines = importedToLines(items, meta);
    setForm(f => ({
      ...f,
      scopes: f.scopes.map(s => s.id === scopeId ? { ...s, line_items: [...realLines(s.line_items), ...lines] } : s)
    }));
    toast.success(`${lines.length} line${lines.length === 1 ? "" : "s"} added from quote`);
  };

  // Header "Quote" import: each group → a new scope or an existing one.
  const addImportedQuotes = (groups) => {
    const opened = {};
    setForm(f => {
      let scopes = [...f.scopes];
      groups.forEach((g) => {
        const lines = importedToLines(g.items, g.meta);
        if (g.target === "__new__") {
          const s = { ...newScope(g.name.trim()), line_items: lines };
          scopes.push(s); opened[s.id] = true;
        } else {
          scopes = scopes.map(s => s.id === g.target ? { ...s, line_items: [...realLines(s.line_items), ...lines] } : s);
          opened[g.target] = true;
        }
      });
      return { ...f, scopes };
    });
    setExpandedScopes(prev => ({ ...prev, ...opened }));
    toast.success(`${groups.length} quote${groups.length === 1 ? "" : "s"} imported`);
  };

  const patchLineItem = (scopeId, itemId, fn) => {
    setForm(f => ({ ...f, scopes: f.scopes.map(s => s.id === scopeId ? { ...s, line_items: s.line_items.map(li => li.id === itemId ? fn(li) : li) } : s) }));
  };
  const duplicateLineItem = (scopeId, itemId) => {
    setForm(f => ({
      ...f,
      scopes: f.scopes.map(s => {
        if (s.id !== scopeId) return s;
        const idx = s.line_items.findIndex(li => li.id === itemId);
        if (idx < 0) return s;
        const copy = { ...s.line_items[idx], id: crypto.randomUUID(), wc_key: undefined };
        return { ...s, line_items: [...s.line_items.slice(0, idx + 1), copy, ...s.line_items.slice(idx + 1)] };
      })
    }));
  };
  // Library Review tag — Apply pulls the library's cost basis onto the line; Keep Mine only resets the baseline.
  const applyLibraryToLine = (scopeId, itemId) => patchLineItem(scopeId, itemId, (li) => { const p = findLibraryPart(li, masterParts); return p ? applyLibraryValues(li, p) : li; });
  const dismissLibraryForLine = (scopeId, itemId) => patchLineItem(scopeId, itemId, (li) => { const p = findLibraryPart(li, masterParts); return p ? acceptLibraryBaseline(li, p) : li; });
  const libraryReviewable = REVIEWABLE_STATUSES.has(form.status);

  // Rail → Margins "Apply to all": overwrite every line's GM% with the proposal defaults (Window Care lines excluded).
  const applyMarginsToAll = () => {
    setForm(f => ({ ...f, scopes: f.scopes.map(s => ({ ...s, line_items: s.line_items.map(li => li.wc_key ? li : withDefaultMargins(li, f.default_margins)) })) }));
    toast.success("Margins applied to every line");
  };

  // iter310 — display only. The jurisdiction rate always applies; this decides whether the CUSTOMER sees a tax line.
  const handleHideTax = (hide) => {
    setForm(f => ({ ...f, hide_tax: hide }));
    if (!proposal?.id) return;
    setHideTax("proposal", proposal.id, hide)
      .then(() => toast.success(hide ? "Tax hidden on this customer's copy" : "Tax will show on this customer's copy"))
      .catch((err) => {
        setForm(f => ({ ...f, hide_tax: !hide }));
        toast.error(err.response?.data?.detail || "Could not change how tax is shown");
      });
  };

  const autoDriveTime = async (scopeId) => {
    try {
      const siteId = form.site_id;
      if (!siteId) { toast.info("Select a site on the proposal first to auto-calculate drive time"); return; }
      const { getDriveTimeForSite } = await import("@/lib/api");
      const res = await getDriveTimeForSite(siteId, true);
      const cost = res.data?.cost_amount || 0;
      const billed = res.data?.billed_amount || 0;
      const miles = res.data?.miles_round_trip || 0;
      const hrs = res.data?.hours_round_trip || 0;
      // iter254 — respect the Settings default drive-time GM% (50 legacy fallback).
      const { getDefaultDriveTimeGm } = await import("@/components/proposals/proposalUtils");
      const defaultGm = getDefaultDriveTimeGm() || 50;
      updateScope(scopeId, "drive_time", cost);
      updateScope(scopeId, "drive_time_gm", cost > 0 ? defaultGm : 0);
      // iter260 — also populate hours (miles ÷ 45mph avg).
      updateScope(scopeId, "drive_time_hours", hrs);
      toast.success(`Drive Time: Cost $${cost} → Billed $${billed} (${miles.toFixed(0)} mi RT, ${hrs.toFixed(2)} hrs)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not auto-calculate drive time");
    }
  };

  const addQualification = () => setForm(f => ({ ...f, qualifications: [...f.qualifications, ""] }));
  const updateQualification = (idx, value) => {
    setForm(f => ({ ...f, qualifications: f.qualifications.map((q, i) => i === idx ? value : q) }));
  };
  const removeQualification = (idx) => {
    setForm(f => ({ ...f, qualifications: f.qualifications.filter((_, i) => i !== idx) }));
  };

  const saveLineItemToLibrary = async (scope, item) => {
    const matchingScope = masterScopes.find(ms => ms.name.toLowerCase() === scope.name.toLowerCase());
    if (!matchingScope) {
      toast.error(`Scope "${scope.name}" not found in library. Create it in Equipment Library first.`);
      return;
    }
    try {
      await createMasterPart({
        scope_id: matchingScope.id,
        equipment_id: matchingScope.id,
        name: item.name,
        description: item.description || "",
        part_cost: parseFloat(item.material_cost) || 0,
        material_margin: parseFloat(item.material_margin) || 50,
        freight: parseFloat(item.freight) || 0,
        labor_hours: parseFloat(item.labor_hours) || 0,
        labor_cost: parseFloat(item.labor_cost) || 40,
        labor_margin: parseFloat(item.labor_margin) || 73.333,
        ...(item.pricing_mode === "flat_rate" ? { pricing_mode: "flat_rate", flat_rate: parseFloat(item.flat_rate) || 0 } : {}),
      });
      toast.success(`"${item.name}" saved to library`);
    } catch {
      toast.error("Failed to save to library");
    }
  };

  const toggleScope = (scopeId) => {
    setExpandedScopes(prev => ({ ...prev, [scopeId]: !prev[scopeId] }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Proposal name is required"); return; }
    if (isNewCustomer) {
      if (!newCustomerData.name.trim()) { toast.error("Customer name is required"); return; }
    } else if (!form.client_id) {
      toast.error("Customer is required"); return;
    }
    setSaving(true);
    // A pending debounced auto-save racing this manual save can create a
    // second proposal — cancel the timer AND bump the seq so any autosave
    // that already fired (but is still awaiting network) becomes stale and
    // its result gets discarded when it finally lands.
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveSeqRef.current++;
    // Serialize with any in-flight autosave — if one is still running,
    // wait for it before starting this one so we don't fire two
    // createProposal calls at once (duplicate rows in DB).
    return runSerializedSave(async () => {
    try {
      let clientId = form.client_id;
      let siteId = form.site_id;
      let finalAddress = form.project_address;
      if (isNewCustomer) {
        if (!finalAddress && !addressOverridden) {
          finalAddress = [newCustomerData.address, newCustomerData.city, newCustomerData.state, newCustomerData.zip_code].filter(Boolean).join(", ");
        }
        const contacts = newCustomerData.contact_name ? [{
          name: newCustomerData.contact_name,
          email: newCustomerData.contact_email || "",
          phone: newCustomerData.contact_phone || "",
          is_primary: true,
        }] : [];
        const clientRes = await createClient({
          name: newCustomerData.name,
          contacts,
          address: newCustomerData.address || "",
          city: newCustomerData.city || "",
          state: newCustomerData.state || "",
          zip_code: newCustomerData.zip_code || "",
        });
        clientId = clientRes.data.id;
        toast.success(`Customer "${newCustomerData.name}" created`);
        // Backend auto-creates a primary "Main" site tied to this client
        // (see routes/clients.py::create_client). Fetch it so every
        // downstream view (PDF, email preview, customer review link,
        // executed PDF) renders with a structured site + contact block.
        try {
          // The /sites endpoint expects `client_id` as a query param.
          // Was previously `getSites(clientId)` — passed as positional
          // arg and got dropped on the floor, causing the primary-site
          // lookup to silently return ALL sites' first result instead
          // of the new customer's, often saving a proposal with no
          // site_id (H7 fix).
          const sitesRes = await getSites({ client_id: clientId });
          const primary = (sitesRes.data || []).find(s => s.is_primary) || (sitesRes.data || [])[0];
          if (primary) siteId = primary.id;
        } catch { /* Non-fatal — proposal still saves without a site_id */ }
        if (onClientCreated) onClientCreated();
        // Duplicate-customer fix: the builder stays open after saving, so
        // flip out of "new customer" mode and select the created client —
        // otherwise every subsequent Save creates the same customer again.
        setClientList(prev => prev.some(c => c.id === clientRes.data.id) ? prev : [...prev, clientRes.data]);
        setIsNewCustomer(false);
        setNewCustomerData({ name: "", contact_name: "", contact_email: "", contact_phone: "", address: "", city: "", state: "", zip_code: "" });
        setForm(f => ({
          ...f,
          client_id: clientId,
          site_id: siteId || f.site_id || "",
          project_address: finalAddress || f.project_address,
          contact_name: f.contact_name || newCustomerData.contact_name || "",
          contact_email: f.contact_email || newCustomerData.contact_email || "",
          contact_phone: f.contact_phone || newCustomerData.contact_phone || "",
        }));
        loadSites(clientId);
      }
      const payload = {
        ...form,
        client_id: clientId,
        site_id: siteId || form.site_id || "",
        project_address: finalAddress || form.project_address,
        value: totals.total,
        qualifications: form.qualifications.filter(q => q.trim()),
        exclusions: form.exclusions.filter(e => e.trim()),
        terms: form.terms.filter(t => t.trim()),
      };
      if (form.prepared_by.trim()) localStorage.setItem("abs_prepared_by", form.prepared_by.trim());
      if (form.prepared_by_email.trim()) localStorage.setItem("abs_prepared_by_email", form.prepared_by_email.trim());
      await onSave(payload);
      setDirty(false);
      setAutoSaveStatus("All changes saved");
      return true;
    } finally {
      setSaving(false);
    }
    });
  };

  const sendProposalEmail = async ({ to, name } = {}) => {
    if (!proposal?.id) {
      toast.error("Save the proposal first before emailing");
      return;
    }

    // iter249 — Force any pending edits to save FIRST. Without this, the
    // PDF backend rebuilds from stale DB data → wrong totals reach the
    // customer in the emailed link.
    try { await flushSave(); }
    catch (e) { toast.error(e.message || "Could not save latest changes — please try again"); return; }

    // Get short_code + review token — always regenerate to ensure freshness
    let shortCode = proposal.short_code || "";
    let reviewToken = proposal.review_token || "";
    if (!shortCode || !reviewToken || includeSignLink) {
      try {
        const res = await generateProposalReviewToken(proposal.id);
        reviewToken = res.data?.token || reviewToken;
        shortCode = res.data?.short_code || shortCode;
      } catch (err) {
        const detail = err?.response?.data?.detail || err?.message || "Unknown error";
        toast.error(`Could not create signable link: ${detail}`);
        return;
      }
    }

    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    // Short link is the primary sharable asset — backend handles the redirect
    // for both the signable review page and the PDF.
    let proposalLink;
    if (includeSignLink) {
      if (shortCode) {
        proposalLink = `${baseUrl}/api/r/p/${shortCode}`;
      } else if (reviewToken) {
        proposalLink = `${window.location.origin}/proposal-review/${reviewToken}`;
      } else {
        toast.error("Could not create a signable link. Please try saving the proposal first.");
        return;
      }
    } else if (shortCode) {
      // View-only PDF short link
      proposalLink = `${baseUrl}/api/r/p/${shortCode}`;
    } else {
      proposalLink = `${baseUrl}/api/proposals/${proposal.id}/pdf`;
    }
    const docName = docFilename("Proposal", proposal.proposal_number || form.name);
    proposalLink = withDocName(proposalLink, docName);
    const attach = docMode === "attach";
    if (attach) downloadPdfUrl(`/proposals/${proposal.id}/pdf?download=1`, { suggestedName: docName, label: "proposal" }).catch(() => toast.error("PDF download failed — send the link instead"));

    // Greeting
    const firstName = name ? name.split(" ")[0] : "";
    const greeting = firstName ? `Dear ${firstName},` : "Dear Customer,";

    const subject = encodeURIComponent(`Proposal: ${form.name || "ABS Proposal"}`);
    const actionText = attach ? "Your proposal is attached" : includeSignLink ? "Please review and sign your proposal here" : "Your proposal is ready";
    const body = encodeURIComponent(
      `${greeting}\n\n${actionText}:\nProposal: ${form.name || ""}\n${attach ? `(${docName} attached)` : proposalLink}\n\nProposal Total: ${formatCurrency(totals.total)}\n${form.project_address ? `Project Address: ${form.project_address}\n` : ""}\nLet us know if you have any questions.`
    );

    // Auto-update status to bid_submitted BEFORE opening the mail app —
    // navigating to a mailto: link makes Chrome cancel every in-flight
    // request, which used to kill this save + the list reload and spam
    // "Failed to load proposals" toasts.
    setShowEmailDialog(false);
    setSelectedContactEmail("");
    if (form.status !== "bid_submitted" && form.status !== "won") {
      setForm(f => ({ ...f, status: "bid_submitted" }));
      try {
        await onSave({ ...form, status: "bid_submitted", value: totals.total, qualifications: form.qualifications.filter(q => q.trim()) }, true);
      } catch { /* non-fatal — the mailto still opens */ }
    }

    window.location.href = `mailto:${to || ""}?subject=${subject}&body=${body}`;
    toast.success(attach ? "PDF downloaded — attach it to the email that just opened" : includeSignLink ? "Email opened with signable link" : "Email opened with proposal link");
  };

  const handleEmailProposal = () => {
    const c = clientContacts.find(x => x.email === selectedContactEmail);
    return sendProposalEmail({ to: selectedContactEmail, name: c?.name });
  };

  const clientContacts = useMemo(() => {
    const client = clientList.find(c => c.id === form.client_id);
    // Show any contact with at least a name, email, or phone — don't filter out by email alone.
    return (client?.contacts || []).filter(c => (c.name && c.name.trim()) || (c.email && c.email.trim()) || (c.phone && c.phone.trim()));
  }, [clientList, form.client_id]);

  // Autosave covers everything once the proposal has a name and a customer; the guard only steps in before that.
  const canAutosave = !!form.name?.trim() && (!!proposal || !!form.client_id || isNewCustomer);
  const guardDialog = useUnsavedGuard({ dirty: dirty && !canAutosave, canSave: false, onSave: handleSave, onDiscard: discardEdits, onLeave: onClose });

  return (
    <div className="space-y-5" data-testid="proposal-builder" onKeyDown={e => { if (e.key === "Enter" && e.target.tagName === "INPUT") e.target.blur(); }}>
      {guardDialog}
      {/* Header Info — row 1: Name · Customer · Bid Due · Customer PO#  (status pill lives on the title row)
          row 2: Site · Address (auto-filled from the site) · Project Type
          row 3: Contact · Email · Phone · AM */}
      {titleSlot && createPortal(
        form.status === "won" ? (
          <span className={`inline-flex h-6 w-[96px] items-center justify-center rounded-full border text-[10px] font-medium ${PROPOSAL_STATUS_COLORS.won}`} data-testid="select-proposal-status">{tr("Won")}</span>
        ) : (
          <PillSelect className="w-[96px] justify-between" value={form.status} onChange={v => setForm(f => ({ ...f, status: v }))} title={tr("Proposal status")}
            options={statusOptions.filter(o => o.value !== "won").map(o => ({ ...o, pill: PROPOSAL_STATUS_COLORS[o.value] }))}
            testId="select-proposal-status" optionTestId={(v) => `proposal-status-opt-${v}`} />
        ), titleSlot)}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <Label className="text-[11px] text-slate-500">Proposal Name *</Label>
          <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Grand County EMS" data-testid="input-proposal-name" />
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">Customer *</Label>
          {isNewCustomer ? (
            <div className="flex items-center gap-1">
              <div className="h-9 flex items-center px-3 bg-green-50 border border-green-200 rounded-md text-sm font-medium text-green-800 flex-1 truncate">
                <UserPlus className="w-3.5 h-3.5 mr-1.5 shrink-0" /> {tr("New Customer")}
              </div>
              <Button type="button" variant="ghost" size="sm" className="h-9 w-9 p-0 text-slate-400 hover:text-red-500" onClick={() => { setIsNewCustomer(false); setNewCustomerData({ name: "", contact_name: "", contact_email: "", contact_phone: "", address: "", city: "", state: "", zip_code: "" }); }} title={tr("Switch to existing customer")}>
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="w-full h-9 justify-between font-normal text-sm" data-testid="select-proposal-client">
                  <span className="truncate">{form.client_id ? (clientList.find(c => c.id === form.client_id)?.name || "Select customer") : "Select customer"}</span>
                  <ChevronDown className="w-4 h-4 ml-2 shrink-0 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-72">
                <div className="p-2">
                  <Input
                    placeholder={tr("Search customers...")}
                    value={customerSearch}
                    onChange={e => setCustomerSearch(e.target.value)}
                    className="h-8 text-sm"
                    data-testid="search-customer-dropdown"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {clientList
                    .filter(c => !customerSearch || c.name.toLowerCase().includes(customerSearch.toLowerCase()))
                    .map(c => (
                      <DropdownMenuItem key={c.id} onClick={() => { setForm(f => ({ ...f, client_id: c.id, site_id: "", hide_tax: !!c.hide_tax })); loadSites(c.id); setCustomerSearch(""); }}>
                        {c.name}
                      </DropdownMenuItem>
                    ))
                  }
                  {clientList.filter(c => !customerSearch || c.name.toLowerCase().includes(customerSearch.toLowerCase())).length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-400">No customers found</div>
                  )}
                </div>
                <div className="border-t p-1">
                  <DropdownMenuItem onClick={() => { setIsNewCustomer(true); setForm(f => ({ ...f, client_id: "", site_id: "" })); setSites([]); setCustomerSearch(""); }} data-testid="btn-new-customer">
                    <UserPlus className="w-4 h-4 mr-2 text-green-600" /> {tr("Create New Customer")}
                  </DropdownMenuItem>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
        <div>
          <Label className="text-[11px] text-slate-500">{tr("Bid Due Date")}</Label>
          <Input type="date" value={form.proposal_date || ""} onChange={e => setForm(f => ({ ...f, proposal_date: e.target.value }))} data-testid="input-proposal-date" />
        </div>
        <div>
          {/* iter305 — prints on the acceptance block and copies to the WO. */}
          <Label className="text-[11px] text-slate-500">{tr("Customer PO #")}</Label>
          <Input value={form.customer_po_number || ""} maxLength={15}
                 onChange={e => setForm(f => ({ ...f, customer_po_number: e.target.value }))}
                 placeholder="if provided" data-testid="input-proposal-po" />
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {!isNewCustomer && (
          <div>
            <Label className="text-[11px] text-slate-500">{tr("Site")}</Label>
            <Select value={form.site_id || "none"} onValueChange={v => {
              const selectedSite = sites.find(s => s.id === v);
              const addr = selectedSite ? [selectedSite.address, selectedSite.city, selectedSite.state, selectedSite.zip_code].filter(Boolean).join(", ") : "";
              setForm(f => ({ ...f, site_id: v === "none" ? "" : v, project_address: addr || f.project_address }));
            }} disabled={!form.client_id}>
              <SelectTrigger data-testid="select-proposal-site"><SelectValue placeholder={tr("Select site")} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{tr("None")}</SelectItem>
                {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {(() => {
          const derivedAddr = isNewCustomer
            ? [newCustomerData.address, newCustomerData.city, newCustomerData.state, newCustomerData.zip_code].filter(Boolean).join(", ")
            : "";
          const displayAddr = form.project_address || derivedAddr;
          const isDefault = !addressOverridden && !form.project_address && derivedAddr;
          return (
            <div className="col-span-2">
              <Label className="text-[11px] text-slate-500">{tr("Address")}</Label>
              <Input
                value={displayAddr}
                onChange={e => {
                  setAddressOverridden(true);
                  setForm(f => ({ ...f, project_address: e.target.value }));
                }}
                placeholder="Auto-fills from the site, or type one"
                className={isDefault ? "text-slate-400" : ""}
                data-testid="input-project-address"
              />
            </div>
          );
        })()}
        <div>
          <Label className="text-[11px] text-slate-500">{tr("Project Type")}</Label>
          <Select value={form.project_type || "service"} onValueChange={handleProjectTypeChange}>
            <SelectTrigger data-testid="select-proposal-project-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PROJECT_TYPES.map(pt => (
                <SelectItem key={pt.value} value={pt.value} data-testid={`proposal-project-type-opt-${pt.value}`}>
                  {tr(pt.label)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {!isNewCustomer && (
          <div>
            <Label className="text-[11px] text-slate-500">{tr("Contact")}</Label>
            <ProposalContactPicker
              clientId={form.client_id}
              siteId={form.site_id}
              contacts={clientContacts}
              value={form.pipeline_contact_id || form.contact_email || form.contact_name}
              onChange={(c) => setForm(f => ({
                ...f,
                contact_email: c?.email || "",
                contact_name: c?.name || "",
                contact_phone: c?.phone || "",
                // iter259 — stamp the picker's stable ID so the PDF
                // resolver can look this contact up on the client
                // record even if the name/email get edited later.
                pipeline_contact_id: c?.id || null,
              }))}
              onContactAdded={(c) => {
                // Merge into clientList so the picker sees it immediately
                setClientList(prev => prev.map(cl => cl.id === form.client_id
                  ? { ...cl, contacts: [...(cl.contacts || []), c] }
                  : cl));
                setForm(f => ({
                  ...f,
                  contact_email: c.email,
                  contact_name: c.name,
                  contact_phone: c.phone || "",
                  pipeline_contact_id: c.id || null,
                }));
              }}
            />
          </div>
        )}
        {!isNewCustomer && (
          <div>
            <Label className="text-[11px] text-slate-500">{tr("Contact Email")}</Label>
            <Input
              type="email"
              value={form.contact_email}
              onChange={e => setForm(f => ({ ...f, contact_email: e.target.value }))}
              placeholder="auto-filled from contact"
              className="h-9 text-sm"
              data-testid="input-proposal-contact-email"
            />
          </div>
        )}
        {!isNewCustomer && (
          <div>
            <Label className="text-[11px] text-slate-500">{tr("Contact Phone")}</Label>
            <Input
              value={form.contact_phone}
              onChange={e => {
                const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                const formatted = digits.length > 6 ? `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}` : digits.length > 3 ? `${digits.slice(0,3)}-${digits.slice(3)}` : digits;
                setForm(f => ({ ...f, contact_phone: formatted }));
              }}
              placeholder="303-555-1234"
              className="h-9 text-sm"
              data-testid="input-proposal-contact-phone"
            />
          </div>
        )}
        <div>
          <Label className="text-[11px] text-slate-500">{tr("Account Manager")}</Label>
          <Select value={form.prepared_by || "__none__"} onValueChange={v => setForm(f => ({ ...f, prepared_by: v === "__none__" ? "" : v }))}>
            <SelectTrigger className="h-9 text-sm" data-testid="input-prepared-by"><SelectValue placeholder={tr("Select AM")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">— None —</SelectItem>
              {/* iter237 — ensure the defaulted creator is selectable/visible
                  even when they aren't in the account_managers list */}
              {form.prepared_by && !accountManagers.some(am => am.name === form.prepared_by) && (
                <SelectItem value={form.prepared_by}>{form.prepared_by}</SelectItem>
              )}
              {assignablePeople(accountManagers, form.prepared_by).map(am => <SelectItem key={am.id} value={am.name}>{am.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* New Customer Form */}
      {isNewCustomer && (
        <div className="bg-green-50/60 border border-green-200 rounded-lg p-4 space-y-3" data-testid="new-customer-form">
          <h4 className="text-xs font-semibold text-emerald-700">{tr("New Customer Details")}</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="col-span-2">
              <Label className="text-[11px] text-slate-500">Customer name *</Label>
              <Input value={newCustomerData.name} onChange={e => setNewCustomerData(d => ({ ...d, name: e.target.value }))} placeholder={tr("Company or customer name")} data-testid="input-new-customer-name" />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">{tr("Contact Name")}</Label>
              <Input value={newCustomerData.contact_name} onChange={e => setNewCustomerData(d => ({ ...d, contact_name: e.target.value }))} placeholder={tr("Primary contact")} data-testid="input-new-customer-contact" />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">{tr("Contact Phone")}</Label>
              <Input value={newCustomerData.contact_phone} onChange={e => {
                const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                const formatted = digits.length > 6 ? `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}` : digits.length > 3 ? `${digits.slice(0,3)}-${digits.slice(3)}` : digits;
                setNewCustomerData(d => ({ ...d, contact_phone: formatted }));
              }} placeholder="303-303-3333" data-testid="input-new-customer-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label className="text-[11px] text-slate-500">{tr("Contact Email")}</Label>
              <Input value={newCustomerData.contact_email} onChange={e => setNewCustomerData(d => ({ ...d, contact_email: e.target.value }))} placeholder="email@example.com" data-testid="input-new-customer-email" />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">Customer Address</Label>
              <Input value={newCustomerData.address} onChange={e => setNewCustomerData(d => ({ ...d, address: e.target.value }))} placeholder={tr("Street address")} data-testid="input-new-customer-address" />
            </div>
            <div>
              <Label className="text-[11px] text-slate-500">{tr("City")}</Label>
              <Input value={newCustomerData.city} onChange={e => {
                const city = e.target.value;
                setNewCustomerData(d => ({ ...d, city }));
                if (city.length > 2 && newCustomerData.state.length >= 2 && !newCustomerData.zip_code) {
                  lookupZip(city, newCustomerData.state).then(r => { if (r.data.zip_code) setNewCustomerData(d => d.zip_code ? d : ({ ...d, zip_code: r.data.zip_code })); }).catch(() => {});
                }
              }} placeholder={tr("City")} data-testid="input-new-customer-city" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[11px] text-slate-500">{tr("State")}</Label>
                <Input value={newCustomerData.state} onChange={e => {
                  const state = e.target.value;
                  setNewCustomerData(d => ({ ...d, state }));
                  if (state.length >= 2 && newCustomerData.city.length > 2 && !newCustomerData.zip_code) {
                    lookupZip(newCustomerData.city, state).then(r => { if (r.data.zip_code) setNewCustomerData(d => d.zip_code ? d : ({ ...d, zip_code: r.data.zip_code })); }).catch(() => {});
                  }
                }} placeholder="CO" data-testid="input-new-customer-state" />
              </div>
              <div>
                <Label className="text-[11px] text-slate-500">{tr("Zip")}</Label>
                <Input value={newCustomerData.zip_code} onChange={e => setNewCustomerData(d => ({ ...d, zip_code: e.target.value }))} placeholder="80216" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Qualifications */}
      <div className="bg-slate-50 rounded-lg p-3 space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold text-slate-700">{tr("Qualifications")}</Label>
          <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={addQualification} data-testid="btn-add-qualification">
            <Plus className="w-3 h-3 mr-1" /> {tr("Add")}
          </Button>
        </div>
        {form.qualifications.map((q, idx) => (
          // eslint-disable-next-line react/no-array-index-key -- editable bullet rows; updateQualification(idx, …) and removeQualification(idx) require positional keys
          <div key={`pq-${idx}`} className="flex items-center gap-2">
            <span className="text-slate-400 text-xs w-4 shrink-0">&#8226;</span>
            <Input
              value={q}
              onChange={e => updateQualification(idx, e.target.value)}
              placeholder={tr("Enter qualification or scope detail...")}
              className="h-8 text-sm flex-1"
              data-testid={`input-qualification-${idx}`}
            />
            {form.qualifications.length > 1 && (
              <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500" onClick={() => removeQualification(idx)}>
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Terms */}
      <div className="bg-slate-50 rounded-lg p-3 space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-sm font-semibold text-slate-700">{tr("Terms")}</Label>
          <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setForm(f => ({ ...f, terms: [...f.terms, ""] }))} data-testid="btn-add-term">
            <Plus className="w-3 h-3 mr-1" /> {tr("Add")}
          </Button>
        </div>
        {form.terms.map((t, idx) => (
          // eslint-disable-next-line react/no-array-index-key -- editable term rows; idx is the update/remove anchor
          <div key={`pt-${idx}`} className="flex items-center gap-2">
            <span className="text-slate-400 text-xs w-4 shrink-0">&#8226;</span>
            <Input
              value={t}
              onChange={ev => { const updated = [...form.terms]; updated[idx] = ev.target.value; setForm(f => ({ ...f, terms: updated })); }}
              placeholder={tr("Enter term...")}
              className="h-8 text-sm flex-1"
              data-testid={`input-term-${idx}`}
            />
            {form.terms.length > 1 && (
              <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500" onClick={() => { const updated = form.terms.filter((_, i) => i !== idx); setForm(f => ({ ...f, terms: updated })); }}>
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
        ))}
      </div>

      {/* Scopes */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Label className="text-sm font-semibold text-slate-700 mr-1">{tr("Scopes & Line Items")}</Label>
          <AddScopesButton masterScopes={masterScopes} onAdd={addScopesFromLibrary} />
          <Button type="button" size="sm" className={BTN.data} onClick={() => setShowImportQuotes(true)} title="Upload one or more vendor quotes — each becomes a scope" data-testid="btn-import-quotes">
            <Upload className="w-3.5 h-3.5 mr-1" /> {tr("Quote")}
          </Button>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-slate-500 num" data-testid="scopes-summary">{form.scopes.length} scope{form.scopes.length === 1 ? "" : "s"} · {(totals.totalHours || 0).toFixed(1)} hrs · {money(totals.total)}</span>
          </div>
        </div>

        {form.scopes.length === 0 && (
          <div className="text-center py-8 border-2 border-dashed border-slate-200 rounded-lg">
            <p className="text-sm text-slate-400">No scopes added yet. Use "+ Scope" to pick from the library, or "Quote" to upload vendor quotes.</p>
          </div>
        )}

        {form.scopes.length > 0 && (
          <EstimateGrid
            scopes={form.scopes} status={form.status} reviewable={libraryReviewable}
            expandedScopes={expandedScopes} toggleScope={toggleScope}
            toggleAllScopes={() => {
              const allExpanded = form.scopes.every(s => expandedScopes[s.id]);
              const next = {};
              form.scopes.forEach(s => { next[s.id] = !allExpanded; });
              setExpandedScopes(next);
            }}
            updateScope={updateScope} updateLineItem={updateLineItem} addLineItem={addLineItem} addLineItemFromPart={addLineItemFromPart} addLineItemsFromParts={addLineItemsFromParts}
            removeLineItem={removeLineItem} removeScope={removeScope} duplicateLineItem={duplicateLineItem} saveLineItemToLibrary={saveLineItemToLibrary}
            applyWindowCare={applyWindowCare}
            applyLibraryToLine={applyLibraryToLine} dismissLibraryForLine={dismissLibraryForLine} addImportedLines={addImportedLines}
            getPartsForScopeName={getPartsForScopeName} masterParts={masterParts} partsStockMap={partsStockMap}
            siteId={form.site_id} siteName={sites.find(s => s.id === form.site_id)?.name} cols={gridCols}
          />
        )}
      </div>

      {/* Global controls — margins · tax & fees · totals (pins to the bottom on wide screens) */}
      <ImportQuotesDialog open={showImportQuotes} onClose={() => setShowImportQuotes(false)} scopes={form.scopes} masterScopes={masterScopes} onAdd={addImportedQuotes} />
      <ProposalRail
        form={form} setForm={setForm} totals={totals} proposalId={proposal?.id}
        canOverrideTax={canOverrideTax} taxOverrideOpen={taxOverrideOpen} setTaxOverrideOpen={setTaxOverrideOpen}
        applyTaxOverrideResult={applyTaxOverrideResult} onHideTax={handleHideTax}
        masterParts={masterParts} reviewable={libraryReviewable} onApplyMargins={applyMarginsToAll}
        updateScope={updateScope} onAutoDrive={autoDriveTime}
      />

      {/* Project Summary (Internal Only) */}
      <div className="rounded-xl border bg-card p-4 space-y-3" data-testid="project-summary-section">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="text-sm font-semibold text-slate-700">{tr("Project Summary")}</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="project-summary-table">
            <thead>
              <tr className="border-b text-[11px] text-slate-500">
                <th className="text-left py-1.5 pr-3 font-semibold">{tr("Cost Type")}</th>
                <th className="text-right py-1.5 px-2 font-semibold">{tr("Revenue")}</th>
                <th className="text-right py-1.5 px-2 font-semibold">{tr("Cost")}</th>
                <th className="text-right py-1.5 px-2 font-semibold">GM $</th>
                <th className="text-right py-1.5 pl-2 font-semibold">{tr("GM %")}</th>
              </tr>
            </thead>
            <tbody className="text-slate-700">
              {[
                { label: "1-Labor", cost: totals.costLabor, revenue: totals.totalLabor },
                { label: "2-Material", cost: totals.costMaterial, revenue: totals.totalMaterial },
                { label: "3-Subcontract", cost: totals.costSubcontract || 0, revenue: totals.totalSubcontract || 0 },
                { label: "4-Freight", cost: totals.costFreight, revenue: totals.totalFreight },
                { label: "5-Equipment", cost: totals.costEquipment, revenue: totals.totalEquipment },
                { label: "6-Labor Burden", cost: 0, revenue: 0 },
                { label: "7-Other", cost: totals.costTravel + totals.costDisposal, revenue: totals.totalTravel + totals.totalDisposal },
                ...(totals.discountAmount > 0 ? [{ label: "Discount", cost: 0, revenue: -totals.discountAmount }] : []),
                { label: "Tax", cost: totals.taxAmount, revenue: totals.taxAmount },
              ].map(row => {
                const gm = row.revenue - row.cost;
                const gmPct = row.revenue > 0 ? (gm / row.revenue) * 100 : 0;
                return (
                  <tr key={row.label} className="border-b border-slate-100" data-testid={`summary-row-${row.label.toLowerCase()}`}>
                    <td className="py-1.5 pr-3 font-medium">{row.label}</td>
                    <td className={`py-1.5 px-2 text-right tabular-nums ${row.revenue < 0 ? "text-red-600" : ""}`}>{formatCurrency(row.revenue)}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{formatCurrency(row.cost)}</td>
                    <td className={`py-1.5 px-2 text-right tabular-nums ${gm < 0 ? "text-red-600" : "text-green-700"}`}>{formatCurrency(gm)}</td>
                    <td className="py-1.5 pl-2 text-right tabular-nums font-semibold">{gmPct > 0 ? `${Math.round(gmPct)}%` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              {(() => {
                // Tax is a pass-through (revenue == cost, GM $0), so it must
                // be included on BOTH sides of the Total row — otherwise GM$
                // is overstated by the tax amount.
                const taxAmt = totals.taxAmount || 0;
                const totalCost = totals.costMaterial + totals.costEquipment + totals.costFreight + totals.costLabor + totals.costTravel + totals.costDisposal + (totals.costSubcontract || 0) + taxAmt;
                const totalRevenue = totals.total;
                const totalGM = totalRevenue - totalCost;
                // GM% measured against pre-tax revenue (true margin).
                const preTaxRevenue = totalRevenue - taxAmt;
                const totalGMPct = preTaxRevenue > 0 ? (totalGM / preTaxRevenue) * 100 : 0;
                return (
                  <tr className="border-t-2 border-slate-300 font-semibold text-slate-900" data-testid="summary-row-total">
                    <td className="py-2 pr-3">{tr("Total")}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{formatCurrency(totalRevenue)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{formatCurrency(totalCost)}</td>
                    <td className={`py-2 px-2 text-right tabular-nums ${totalGM < 0 ? "text-red-600" : "text-green-700"}`}>{formatCurrency(totalGM)}</td>
                    <td className="py-2 pl-2 text-right tabular-nums">{totalGMPct > 0 ? `${Math.round(totalGMPct)}%` : "—"}</td>
                  </tr>
                );
              })()}
            </tfoot>
          </table>
        </div>
      </div>

      {/* Customer Notes — live from the customer record (iter241) */}
      <CustomerNotesPanel clientId={form.client_id} />

      {/* Follow-Up Activity — auto/manual sends + reply auto-pauses (iter243) */}
      <FollowupActivityLog proposalId={proposal?.id} />

      {/* Notes */}
      <div>
        <Label className="text-[11px] text-slate-500">{tr("Internal Notes")}</Label>
        <Textarea value={form.notes || ""} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder={tr("Internal notes (not shown on customer proposal)")} className="text-sm" data-testid="input-proposal-notes" />
      </div>

      {/* Executed Proposal Drop Zone */}
      {proposal && (
        <div className="pt-3" data-testid="proposal-executed-dropzone-section">
          <ExecutedDocDropZone
            entityId={proposal.id}
            entityType="proposal"
            executedDocument={proposal.executed_document}
            /* iter262 — instead of calling `uploadExecutedProposal`
               straight through, we open the Foundation-choice modal
               first, then upload with the selected sync mode. */
            onUpload={(id, formData) => new Promise((resolve, reject) => {
              const file = formData.get("file");
              if (!file) { reject(new Error("No file")); return; }
              setSignedPdfSyncMode("skipped");
              setSignedPdfPending({ id, file, resolve, reject });
            })}
            onViewUrl={getExecutedProposalDocument}
            onStatusChange={() => { setForm(f => ({ ...f, status: "won", signature_required: false })); }}
          />
        </div>
      )}

      {/* iter262 — Foundation-sync-mode picker for the drop-signed-PDF flow. */}
      <Dialog
        open={!!signedPdfPending}
        onOpenChange={(o) => {
          if (!o && signedPdfPending) {
            signedPdfPending.reject(new Error("cancelled"));
            setSignedPdfPending(null);
          }
        }}
      >
        <DialogContent className="max-w-md" data-testid="dialog-signed-pdf-foundation">
          <DialogHeader>
            <DialogTitle>Mark Won — Foundation sync?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-slate-600">
              {tr("Uploading")} <span className="font-semibold">{signedPdfPending?.file?.name}</span> will mark this proposal <span className="font-semibold">{tr("Won")}</span> and create a Work Order. Choose how to handle Foundation:
            </p>
            <div className="space-y-2">
              <label className="flex items-start gap-2 p-2 border border-slate-200 rounded cursor-pointer hover:bg-slate-50" data-testid="radio-signed-pdf-skipped">
                <input type="radio" name="signed-pdf-sync-mode" value="skipped" checked={signedPdfSyncMode === "skipped"} onChange={() => setSignedPdfSyncMode("skipped")} className="mt-1" />
                <div>
                  <div className="font-medium text-slate-800">{tr("In-app only")}</div>
                  <div className="text-xs text-slate-500">Create the WO in ServiceOps only. Foundation is skipped — the queued-push job will leave it alone.</div>
                </div>
              </label>
              <label className="flex items-start gap-2 p-2 border border-slate-200 rounded cursor-pointer hover:bg-slate-50" data-testid="radio-signed-pdf-queued">
                <input type="radio" name="signed-pdf-sync-mode" value="queued" checked={signedPdfSyncMode === "queued"} onChange={() => setSignedPdfSyncMode("queued")} className="mt-1" />
                <div>
                  <div className="font-medium text-slate-800">{tr("Queue for Foundation")}</div>
                  <div className="text-xs text-slate-500">Create the WO and queue it for the Foundation bridge to push on the next sync run.</div>
                </div>
              </label>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => { signedPdfPending?.reject(new Error("cancelled")); setSignedPdfPending(null); }}
                data-testid="btn-signed-pdf-cancel"
              >
                {tr("Cancel")}
              </Button>
              <Button
                onClick={async () => {
                  const pending = signedPdfPending;
                  if (!pending) return;
                  try {
                    const fd = new FormData();
                    fd.append("file", pending.file);
                    fd.append("foundation_sync_mode", signedPdfSyncMode);
                    const res = await uploadExecutedProposal(pending.id, fd);
                    pending.resolve(res);
                  } catch (err) {
                    pending.reject(err);
                  } finally {
                    setSignedPdfPending(null);
                  }
                }}
                data-testid="btn-signed-pdf-confirm"
              >
                {tr("Confirm & Upload")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Actions — extracted to ProposalBuilderActionBar (iter160) */}
      <ProposalBuilderActionBar
        form={form}
        proposal={proposal}
        saving={saving}
        totals={totals}
        autoSaveStatus={autoSaveStatus}
        autoSaveTimer={autoSaveTimer}
        setAutoSaveStatus={setAutoSaveStatus}
        confirmCancel={confirmCancel}
        setConfirmCancel={setConfirmCancel}
        setShowPreview={setShowPreview}
        setShowEmailDialog={setShowEmailDialog}
        openPreviewFlush={openPreviewFlush}
        openEmailDialogFlush={openEmailDialogFlush}
        flushSave={flushSave}
        clientList={clientList}
        sites={sites}
        onClose={onClose}
        onSave={onSave}
        onConvertToWO={onConvertToWO}
        returnToWO={returnToWO}
        onReturnToWO={onReturnToWO}
        handleSave={handleSave}
      />

      {/* Proposal Preview Modal */}
      {showPreview && (
        <ProposalPreview
          form={form}
          proposalId={proposal?.id}
          clients={clientList}
          sites={sites}
          totals={totals}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* Email Proposal Dialog — extracted to ProposalEmailDialog */}
      <ProposalEmailDialog
        open={showEmailDialog}
        onOpenChange={setShowEmailDialog}
        clientContacts={clientContacts}
        selectedContactId={selectedContactId}
        setSelectedContactId={setSelectedContactId}
        selectedContactEmail={selectedContactEmail}
        setSelectedContactEmail={setSelectedContactEmail}
        showAddContactInline={showAddContactInline}
        setShowAddContactInline={setShowAddContactInline}
        includeSignLink={includeSignLink}
        setIncludeSignLink={setIncludeSignLink}
        docMode={docMode} setDocMode={setDocMode}
        proposal={proposal}
        form={form}
        clientList={clientList}
        setClientList={setClientList}
        sites={sites}
        totals={totals}
        onSend={handleEmailProposal}
      />
    </div>
  );
}

// ============== PROPOSAL PREVIEW ==============

export default function Proposals() {
  // iter250 — hard-delete of duplicate Won proposals follows Proposals: write.
  const { can, user } = useAuth();
  const canHardDelete = can("sales.proposals", "write");
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const [proposals, setProposals] = useState([]);
  const [archivedProposals, setArchivedProposals] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [preparedByFilter, setPreparedByFilter] = useState("all");
  const [propCats, setPropCats] = useStickyCategories("abs.proposals.categoryFilter");
  const [accountManagers, setAccountManagers] = useState([]);
  const [sortField, setSortField] = useState("proposal_date");
  const [sortDirection, setSortDirection] = useState("desc");
  const [view, setView] = useProposalView(user?.id);
  const [groupBy, setGroupBy] = useState("");
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingProposal, setEditingProposal] = useState(null);
  const [masterScopes, setMasterScopes] = useState([]);
  const [masterEquipment, setMasterEquipment] = useState([]);
  const [masterParts, setMasterParts] = useState([]);
  const [prefill, setPrefill] = useState(null);
  const [pendingEditId, setPendingEditId] = useState(null);

  // Close builder when nav tab is clicked (including clicking "Proposals" again)
  useEffect(() => {
    if (location.state?.reset && builderOpen) {
      setBuilderOpen(false);
      setEditingProposal(null);
      setPrefill(null);
    }
    // Triggered only by the route-level reset signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.reset]);

  // Handle URL params (from Customer tab's "Add Proposal" or "Edit Proposal")
  useEffect(() => {
    const clientId = searchParams.get("client_id");
    const editId = searchParams.get("edit_id");
    if (editId) {
      setPendingEditId(editId);
      setSearchParams({}, { replace: true });
    } else if (clientId) {
      const siteId = searchParams.get("site_id") || "";
      setPrefill({ client_id: clientId, site_id: siteId });
      setBuilderOpen(true);
      setEditingProposal(null);
      setSearchParams({}, { replace: true });
    }
    // `setSearchParams` is stable from react-router-dom.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Handle viewProposalId from WO "Converted from Proposal" link
  const [returnToWO, setReturnToWO] = useState(null);
  useEffect(() => {
    if (location.state?.viewProposalId) {
      setPendingEditId(location.state.viewProposalId);
      if (location.state.returnToWO) setReturnToWO(location.state.returnToWO);
      // Clear the state so refreshing doesn't reopen
      navigate("/proposals", { replace: true, state: {} });
    }
    // `navigate` is stable; we only want viewProposalId to retrigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.viewProposalId]);

  // Open proposal when pendingEditId is set and proposals are loaded
  useEffect(() => {
    if (pendingEditId && (proposals.length > 0 || archivedProposals.length > 0)) {
      const found = proposals.find(p => p.id === pendingEditId) || archivedProposals.find(p => p.id === pendingEditId);
      if (found) {
        setEditingProposal(found);
        setBuilderOpen(true);
        setPendingEditId(null);
      } else {
        // Proposal not found yet — reload data to pick up newly created proposals
        loadData();
      }
    }
    // `loadData` is stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingEditId, proposals, archivedProposals]);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search]);

  useEffect(() => {
    loadLibrary();
  }, []);

  const loadLibrary = async () => {
    try {
      const [scopeRes, equipRes, partsRes, amRes] = await Promise.all([
        getMasterScopes(),
        getMasterEquipment(),
        getMasterParts(),
        getAccountManagers(),
      ]);
      setMasterScopes(scopeRes.data);
      setMasterEquipment(equipRes.data);
      setMasterParts(partsRes.data);
      setAccountManagers(amRes.data);
    } catch {}
  };

  const loadData = async (attempt = 0) => {
    try {
      const params = {};
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      else params.status = "open_active";
      if (search) params.search = search;

      const [proposalsRes, archivedRes, clientsRes] = await Promise.all([
        getProposals(params),
        getProposals({ status: "archived", limit: 2000, ...(search ? { search } : {}) }),
        getClients(),
      ]);
      setProposals(proposalsRes.data);
      setArchivedProposals(archivedRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      // No response = request aborted (e.g. Chrome cancels in-flight XHRs
      // when a mailto: navigation starts) — retry silently instead of
      // spamming an error toast for a transient cancellation.
      if (!error.response && attempt < 2) {
        setTimeout(() => loadData(attempt + 1), 1200);
        return;
      }
      toast.error("Failed to load proposals");
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDirection(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDirection("asc"); }
  };

  const sortedProposals = useMemo(() => {
    let filtered = view === "archived" ? archivedProposals.filter(p => (p.status === "won" || p.status === "lost") && (statusFilter === "all" || p.status === statusFilter)) : proposals;
    if (preparedByFilter && preparedByFilter !== "all") {
      filtered = filtered.filter(p => (p.prepared_by || "").toLowerCase() === preparedByFilter.toLowerCase());
    }
    // iter305 — category = the scope section header (scopes[].name).
    if (propCats.length) filtered = filtered.filter(p => matchesCategories(p, propCats));
    if (!sortField) return filtered;
    // Helpers for the joined-column sorts (customer, contact) that
    // have to hop from `proposals` → `clients` and mirror the same
    // resolution rule PipelineCard uses to pick the "active" contact.
    const clientNameOf = (p) => (clients.find(c => c.id === p.client_id)?.name || "").toLowerCase();
    const contactNameOf = (p) => {
      const contacts = clients.find(c => c.id === p.client_id)?.contacts || [];
      const active = contacts.find(c => c.id === p.pipeline_contact_id)
                  || contacts.find(c => c.is_primary)
                  || contacts[0];
      return (active?.name || "").toLowerCase();
    };
    return [...filtered].sort((a, b) => {
      let aVal, bVal;
      switch (sortField) {
        case "name": aVal = (a.name || "").toLowerCase(); bVal = (b.name || "").toLowerCase(); break;
        case "customer": aVal = clientNameOf(a); bVal = clientNameOf(b); break;
        case "contact": aVal = contactNameOf(a); bVal = contactNameOf(b); break;
        case "am": aVal = (a.prepared_by || "").toLowerCase(); bVal = (b.prepared_by || "").toLowerCase(); break;
        case "value": aVal = a.value || 0; bVal = b.value || 0; break;
        case "created_at": aVal = a.created_at || ""; bVal = b.created_at || ""; break;
        case "next_action_date": aVal = a.next_action_date || "9999"; bVal = b.next_action_date || "9999"; break;
        case "proposal_date": aVal = a.proposal_date || "9999"; bVal = b.proposal_date || "9999"; break;
        case "probability": aVal = a.probability ?? 50; bVal = b.probability ?? 50; break;
        case "status": aVal = PROPOSAL_STATUS_ORDER.indexOf(a.status || "draft"); bVal = PROPOSAL_STATUS_ORDER.indexOf(b.status || "draft"); break;
        case "gm_pct": { const ga = gmOf(a), gb = gmOf(b); aVal = ga != null && a.value > 0 ? ga / a.value : -Infinity; bVal = gb != null && b.value > 0 ? gb / b.value : -Infinity; break; }
        default: return 0;
      }
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [proposals, archivedProposals, view, statusFilter, clients, sortField, sortDirection, preparedByFilter, propCats]);

  // Get unique prepared_by values for filter dropdown
  const preparedByOptions = useMemo(() => {
    const names = new Set();
    proposals.forEach(p => { if (p.prepared_by) names.add(p.prepared_by); });
    archivedProposals.forEach(p => { if (p.prepared_by) names.add(p.prepared_by); });
    return [...names].sort();
  }, [proposals, archivedProposals]);

  // Weighted Pipeline Summary + Win Ratio
  const pipelineSummary = useMemo(() => {
    const active = proposals.filter(p => p.status !== "won" && p.status !== "lost");
    const totalValue = active.reduce((s, p) => s + (p.value || 0), 0);
    const weightedValue = active.reduce((s, p) => s + (p.value || 0) * ((p.probability ?? 50) / 100), 0);
    const avgProbability = active.length > 0 ? Math.round(active.reduce((s, p) => s + (p.probability ?? 50), 0) / active.length) : 0;
    const overdueCount = active.filter(p => p.next_action_date && p.next_action_date < new Date().toISOString().split("T")[0]).length;
    // Win ratio from archived
    const wonCount = archivedProposals.filter(p => p.status === "won").length;
    const lostCount = archivedProposals.filter(p => p.status === "lost").length;
    const totalDecided = wonCount + lostCount;
    const winRate = totalDecided > 0 ? Math.round((wonCount / totalDecided) * 100) : 0;
    return { count: active.length, totalValue, weightedValue, avgProbability, overdueCount, wonCount, lostCount, winRate, totalDecided };
  }, [proposals, archivedProposals]);

  // ── iter203: bulk selection + bulk pipeline updates ──
  // Set of proposal IDs the operator has checked. Cleared whenever
  // filters/search change so a hidden-but-selected row can't be
  // accidentally mutated by a bulk action.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkAM, setBulkAM] = useState("");

  useEffect(() => {
    // Clear selection whenever the visible list changes materially.
    setSelectedIds(new Set());
  }, [statusFilter, search, preparedByFilter, propCats]);

  const toggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const visibleIds = useMemo(() => sortedProposals.map(p => p.id), [sortedProposals]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some(id => selectedIds.has(id));

  const toggleSelectAll = useCallback(() => {
    setSelectedIds(prev => {
      if (visibleIds.every(id => prev.has(id))) return new Set();
      return new Set(visibleIds);
    });
  }, [visibleIds]);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const handleBulkApply = useCallback(async () => {
    if (selectedIds.size === 0) { toast.error("No proposals selected"); return; }
    if (!bulkStatus && !bulkAM) { toast.error("Choose a status or AM to apply"); return; }
    setBulkBusy(true);
    try {
      const payload = { proposal_ids: [...selectedIds] };
      if (bulkStatus) payload.status = bulkStatus;
      if (bulkAM) {
        payload.prepared_by = bulkAM;
        const am = accountManagers.find(a => a.name === bulkAM);
        payload.prepared_by_email = am?.email || "";
      }
      const res = await bulkUpdateProposalsPipeline(payload);
      const { updated, skipped } = res.data || {};
      const skippedCount = Array.isArray(skipped) ? skipped.length : 0;
      if (skippedCount > 0) {
        toast.success(`Updated ${updated}, skipped ${skippedCount} (already won or not found)`);
      } else {
        toast.success(`Updated ${updated} proposal${updated === 1 ? "" : "s"}`);
      }
      setBulkStatus("");
      setBulkAM("");
      clearSelection();
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkBusy(false);
    }
    // `loadData` is stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds, bulkStatus, bulkAM, accountManagers, clearSelection]);

  // iter248 — Track the current editing proposal via ref in addition to
  // state. React's setState is async — a serialized autosave that runs
  // immediately after a create can still see the STALE state closure
  // (editingProposal=null) and fire a SECOND createProposal, producing
  // duplicate rows. The ref is written SYNCHRONOUSLY inside handleSave
  // so the next queued call sees the id right away.
  const editingProposalRef = useRef(null);
  useEffect(() => { editingProposalRef.current = editingProposal; }, [editingProposal]);

  const handleSave = async (payload, silent = false) => {
    try {
      // iter250 — Prefer explicit id from the payload (unmount / flush
      // path). Async .then() chains can fire AFTER editingProposalRef
      // has been reset to null by the close handler; without an
      // explicit id the flush would create a duplicate row.
      const flushId = payload && payload._flush_proposal_id;
      const current = editingProposalRef.current || (flushId ? { id: flushId } : null);
      // strip the internal hint before sending to the API
      const clean = payload && payload._flush_proposal_id
        ? (() => { const { _flush_proposal_id, ...rest } = payload; return rest; })()
        : payload;
      if (current) {
        const res = await updateProposal(current.id, clean);
        if (!silent) toast.success("Proposal saved");
        if (res?.data) {
          editingProposalRef.current = res.data;
          setEditingProposal(res.data);
        }
        loadData();
      } else {
        const res = await createProposal(clean);
        if (!silent) toast.success("Proposal created");
        editingProposalRef.current = res.data;
        setEditingProposal(res.data);
        loadData();
      }
    } catch (error) {
      if (!silent) toast.error("Failed to save proposal");
      else throw error;
    }
  };

  const handleEdit = (proposal) => {
    setEditingProposal(proposal);
    setBuilderOpen(true);
  };

  const openNewProposal = () => {
    setPrefill(null);
    setEditingProposal(null);
    setBuilderOpen(true);
  };

  const handleInlineStatusChange = async (proposalId, newStatus) => {
    try {
      const proposal = proposals.find(p => p.id === proposalId);
      if (!proposal) return;
      await updateProposal(proposalId, { ...proposal, status: newStatus });
      toast.success("Status updated");
      loadData();
    } catch { toast.error("Failed to update status"); }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Archive this proposal? It will be moved to the Archived section.")) {
      try {
        await deleteProposal(id);
        toast.success("Proposal archived");
        loadData();
      } catch { toast.error("Failed to archive proposal"); }
    }
  };

  const navigate = useNavigate();

  const handleConvertToWO = async (proposal, opts = {}) => {
    try {
      const res = await convertProposalToWO(proposal.id, opts);
      const mode = res?.data?.foundation_sync_mode || opts.foundation_sync_mode || "skipped";
      toast.success(`Won → Work Order ${res.data.wo_number} created (${mode === "queued" ? "queued for Foundation" : "in-app only"})`);
      setBuilderOpen(false);
      setEditingProposal(null);
      loadData();
      // Navigate directly to the WO
      navigate(`/clients`, { state: { openClientId: proposal.client_id, openTab: "workorders", openWorkOrderId: res.data.work_order_id }, replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to convert proposal");
    }
  };

  const getClient = (clientId) => clients.find(c => c.id === clientId);
  const getClientName = (clientId) => getClient(clientId)?.name || "-";

  const hdr = { sortField, sortDirection, onSort: handleSort };
  const archivedItems = archivedProposals.filter(p => (p.status === "won" || p.status === "lost") && matchesCategories(p, propCats));
  const viewItems = view === "archived" ? archivedItems : sortedProposals;
  const viewTotal = viewItems.reduce((s, p) => s + (p.value || 0), 0);
  const amPills = activePeople(accountManagers);
  const groupedProposals = useMemo(() => {
    if (!groupBy) return [{ key: "", label: "", items: sortedProposals }];
    const labelOf = (p) => groupBy === "am" ? (p.prepared_by || "No AM")
      : groupBy === "status" ? (statusOptions.find(o => o.value === (p.status || "draft"))?.label || p.status)
      : (clients.find(c => c.id === p.client_id)?.name || "No customer");
    const map = new Map();
    sortedProposals.forEach(p => { const l = labelOf(p); if (!map.has(l)) map.set(l, []); map.get(l).push(p); });
    // Status groups follow the workflow order; AM / Customer groups are alphabetical.
    const rank = (label) => groupBy === "status" ? PROPOSAL_STATUS_ORDER.indexOf(statusOptions.find(o => o.label === label)?.value) : 0;
    return [...map.entries()].sort((a, b) => (rank(a[0]) - rank(b[0])) || a[0].localeCompare(b[0])).map(([label, items]) => ({ key: label, label, items }));
  }, [groupBy, sortedProposals, clients]);

  // Builder view (full page)
  if (builderOpen) {
    return (
      <div data-testid="proposals-page">
        <div className="data-table-container !overflow-visible">
          <div className="flex items-center gap-2 mb-4" data-testid="proposal-builder-title">
            <button type="button" onClick={() => { setBuilderOpen(false); setEditingProposal(null); setPrefill(null); }}
              className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800" title={tr("Back to proposals")} data-testid="btn-builder-back">
              <ArrowLeft className="w-4 h-4" />
            </button>
            {editingProposal?.proposal_number && <span className="font-mono text-sm text-brand">{editingProposal.proposal_number}</span>}
            <h2 className="text-lg font-semibold text-slate-900 truncate">{editingProposal ? (editingProposal.name || "Edit proposal") : "New proposal"}</h2>
            {/* Builder portals its status pill here so it sits on the title row, not in the form grid. */}
            <div id="proposal-title-right" className="ml-auto mr-2 flex items-center" data-testid="proposal-title-right" />
          </div>
          {/* Phase 2: automated email history (renders only when ≥1 send/skip exists) */}
          {editingProposal?.id && (
            <div className="mb-4">
              <EmailHistoryPanel refType="proposal" refId={editingProposal.id} />
            </div>
          )}
          <ProposalBuilder
            proposal={editingProposal}
            clients={clients}
            onSave={handleSave}
            onClose={() => { setBuilderOpen(false); setEditingProposal(null); setPrefill(null); }}
            masterScopes={masterScopes}
            masterEquipment={masterEquipment}
            masterParts={masterParts}
            prefill={prefill}
            onClientCreated={() => loadData()}
            onConvertToWO={handleConvertToWO}
            returnToWO={returnToWO}
            onReturnToWO={() => {
              if (returnToWO) {
                navigate("/clients", { state: { openClientId: returnToWO.clientId, openTab: "workorders", openWorkOrderId: returnToWO.woId } });
                setReturnToWO(null);
              }
            }}
            accountManagers={accountManagers}
          />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="proposals-page" className="flex flex-col gap-2">
      <StickyTop testId="proposals-sticky-top">
      <div className="flex shrink-0 flex-wrap items-center gap-2" data-testid="proposals-toolbar">
        <SearchBar value={search} onChange={setSearch} testId="search-proposals" width="w-64" />

        {/* One rail: group pills · | · Won · Lost  (AM filter lives in the AM column header) */}
        <PillRail testId="proposals-view-pills">
          <span className="inline-flex items-center gap-1" data-testid="proposals-group-by">
            {[["am", "AM"], ["status", "Status"], ["customer", "Customer"]].map(([k, l]) => (
              <Pill key={k} on={groupBy === k} testId={`proposals-group-${k}`} title={groupBy === k ? "Stop grouping" : `Group by ${l}`}
                onClick={() => setGroupBy(groupBy === k ? "" : k)}>{l}</Pill>
            ))}
          </span>
          <PillDivider />
          <Pill on={view === "archived"} testId="toggle-archived-proposals" title={view === "archived" ? "Back to open proposals" : "Show won and lost proposals"}
            onClick={() => { setView(view === "archived" ? "open" : "archived"); setStatusFilter("all"); }}>{tr("Archived")}</Pill>
        </PillRail>

        <button type="button" onClick={openNewProposal} className={btn("write")} aria-label={tr("New Proposal")} title={tr("New Proposal")} data-testid="btn-add-proposal">
          <Plus className="w-4 h-4" />
        </button>

        <div className="ml-auto flex items-center gap-1.5 text-xs text-slate-500 num" data-testid="proposals-count">
          <span>{viewItems.length} {view === "open" ? (viewItems.length === 1 ? "proposal" : "proposals") : `${view} ${viewItems.length === 1 ? "proposal" : "proposals"}`} · {money(viewTotal)}</span>
          {view === "open" && (
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" className="rounded p-0.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100" title="Pipeline numbers: weighted value, average probability, win rate, overdue actions" data-testid="pipeline-summary">
                  <Info className="h-3.5 w-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-60 p-3 text-xs">
                <div className="text-[11px] font-semibold text-slate-500 mb-2">{tr("Pipeline")}</div>
                <dl className="grid grid-cols-[1fr_auto] gap-y-1.5 gap-x-3">
                  <dt className="text-slate-500">{tr("Open")}</dt><dd className="num font-semibold" data-testid="pipeline-count">{pipelineSummary.count}</dd>
                  <dt className="text-slate-500">{tr("Total pipeline")}</dt><dd className="num font-semibold" data-testid="pipeline-total">{money(pipelineSummary.totalValue)}</dd>
                  <dt className="text-slate-500">{tr("Weighted value")}</dt><dd className="num font-semibold text-emerald-700" data-testid="pipeline-weighted">{money(pipelineSummary.weightedValue)}</dd>
                  <dt className="text-slate-500">{tr("Avg probability")}</dt><dd className="num font-semibold" data-testid="pipeline-avg-prob">{pipelineSummary.avgProbability}%</dd>
                  <dt className="text-slate-500">{tr("Win rate")}</dt>
                  <dd className="num font-semibold" data-testid="pipeline-win-rate">{pipelineSummary.totalDecided > 0 ? `${pipelineSummary.winRate}%` : "—"} <span className="font-normal text-slate-400">({pipelineSummary.wonCount}W / {pipelineSummary.lostCount}L)</span></dd>
                  <dt className="text-slate-500">{tr("Overdue actions")}</dt><dd className={`num font-semibold ${pipelineSummary.overdueCount > 0 ? "text-red-600" : ""}`} data-testid="pipeline-overdue">{pipelineSummary.overdueCount}</dd>
                </dl>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
      </StickyTop>

      <div className="data-table-container">
        {(view === "open" || view === "archived") && (<>
        {/* Bulk action bar (iter203) — visible when any row is selected */}
        {selectedIds.size > 0 && (
          <div
            className="sticky top-0 z-10 bg-slate-50 border-b border-slate-200 px-3 py-2 flex items-center gap-2 flex-wrap"
            data-testid="pipeline-bulk-bar"
          >
            <span className="text-xs font-semibold text-slate-800" data-testid="pipeline-bulk-count">
              {selectedIds.size} selected
            </span>
            <span className="text-slate-300">·</span>
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-slate-500 font-semibold">{tr("Set Status")}</span>
              <Select value={bulkStatus} onValueChange={setBulkStatus}>
                <SelectTrigger className="h-7 w-36 text-xs" data-testid="pipeline-bulk-status">
                  <SelectValue placeholder="No Change" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.filter(o => o.value !== "won").map(o => (
                    <SelectItem key={o.value} value={o.value} data-testid={`pipeline-bulk-status-opt-${o.value}`}>
                      {tr(o.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[11px] text-slate-500 font-semibold">{tr("Set AM")}</span>
              <Select value={bulkAM} onValueChange={setBulkAM}>
                <SelectTrigger className="h-7 w-40 text-xs" data-testid="pipeline-bulk-am">
                  <SelectValue placeholder="No Change" />
                </SelectTrigger>
                <SelectContent>
                  {activePeople(accountManagers).map(am => (
                    <SelectItem key={am.id} value={am.name} data-testid={`pipeline-bulk-am-opt-${am.id}`}>
                      {am.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <button type="button" className={btn("write")} onClick={handleBulkApply} disabled={bulkBusy || (!bulkStatus && !bulkAM)}
              title="Apply the chosen status / AM to every selected proposal" data-testid="pipeline-bulk-apply">
              {bulkBusy ? "Applying…" : "Apply"}
            </button>
            <button type="button" className={btn("nav")} onClick={clearSelection} title={tr("Clear the selection")} data-testid="pipeline-bulk-clear">
              {tr("Clear")}
            </button>
          </div>
        )}

        {/* Pipeline table — same chrome as the Work Orders list (thead-sticky, text-xs cells, badge-style status) */}
        <Table className="table-fixed" data-testid="proposals-table">
          <TableHeader className="thead-sticky">
            <TableRow>
              <TableHead className="w-[36px] text-center">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  ref={el => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                  onChange={toggleSelectAll}
                  className="h-4 w-4 cursor-pointer rounded border-slate-300 text-brand focus:ring-brand"
                  aria-label={tr("Select All Visible Proposals")}
                  data-testid="pipeline-select-all"
                />
              </TableHead>
              <Th {...hdr} field="name" className="w-[24%]" testId="proposals-sort-name" filter={
                <CategoryFilter records={[...proposals, ...archivedProposals]} value={propCats} onChange={setPropCats}
                                testId="proposal-category-filter" iconOnly label="category (scope)" />
              }>{tr("Proposal")}</Th>
              <Th {...hdr} field="customer" testId="proposals-sort-customer">{tr("Customer")}</Th>
              <Th {...hdr} field="am" className="w-[60px]" testId="proposals-sort-am" filter={
                <HeaderFilter label="AM" testId="filter-prepared-by"
                  options={amPills.map(am => ({ value: am.name, label: am.name.split(" ")[0] }))}
                  value={preparedByFilter === "all" ? "" : preparedByFilter} onChange={(v) => setPreparedByFilter(v || "all")} />
              }>AM</Th>
              <Th {...hdr} field="status" className="w-[100px]" testId="proposals-sort-status" filter={
                <HeaderFilter label={tr("Status")} testId="filter-status"
                  options={(view === "archived" ? statusOptions.filter(o => o.value === "won" || o.value === "lost") : statusOptions.filter(o => o.value !== "won" && o.value !== "lost")).map(o => ({ value: o.value, label: o.label }))}
                  value={statusFilter === "all" ? "" : statusFilter} onChange={(v) => setStatusFilter(v || "all")} />
              }>{tr("Status")}</Th>
              <Th {...hdr} field="value" className="w-[90px]" testId="proposals-sort-value">{tr("Total Sales")}</Th>
              <Th {...hdr} field="gm_pct" className="w-[56px] text-center tabular-nums" testId="proposals-sort-gm-pct">{tr("GM %")}</Th>
              <Th {...hdr} field="proposal_date" className="w-[68px] num" testId="proposals-sort-date">{tr("Due")}</Th>
              <Th {...hdr} field="contact" testId="proposals-sort-contact">{tr("Contact")}</Th>
              <TableHead className="w-[88px] whitespace-nowrap text-xs font-semibold">{tr("Phone")}</TableHead>
              <Th {...hdr} field="next_action_date" className="w-[44px] text-center" testId="proposals-sort-action">{tr("Action")}</Th>
              <TableHead className="w-[56px] whitespace-nowrap text-center text-xs font-semibold">{tr("Notes")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={12} className="py-8 text-center"><div className="mx-auto h-6 w-6 animate-spin rounded-full border-b-2 border-brand" /></TableCell></TableRow>
            ) : sortedProposals.length === 0 ? (
              <TableRow><TableCell colSpan={12} className="py-8 text-center text-sm text-slate-400" data-testid="proposals-empty">{search.trim() ? (archivedItems.length ? "No open matches — see archived matches below" : "No matches") : view === "archived" ? "No archived proposals" : "No proposals found — create your first proposal to get started"}</TableCell></TableRow>
            ) : (
              groupedProposals.map(g => (
                <Fragment key={g.key || "all"}>
                  {g.label && (
                    <TableRow className="bg-slate-50 hover:bg-slate-50" data-testid={`proposals-group-header-${g.key}`}>
                      <TableCell colSpan={12} className="py-1.5 text-[11px] font-semibold text-slate-600">
                        {g.label} <span className="font-normal text-slate-400 num">{g.items.length} · {money(g.items.reduce((s, p) => s + (p.value || 0), 0))}</span>
                      </TableCell>
                    </TableRow>
                  )}
                  {g.items.map(proposal => (
                    <PipelineCard
                      key={proposal.id}
                      proposal={proposal}
                      client={getClient(proposal.client_id)}
                      onEdit={handleEdit}
                      onContactAdded={loadData}
                      onNavigateToClient={(clientId) => navigate("/clients", { state: { openClientId: clientId, returnTo: "/proposals" } })}
                      onProbabilityChange={(propId, newProb) => {
                        setProposals(prev => prev.map(p => p.id === propId ? { ...p, probability: newProb } : p));
                      }}
                      onBidDateChange={(propId, date) => {
                        setProposals(prev => prev.map(p => p.id === propId ? { ...p, proposal_date: date, followup_count: 0, last_followup_at: null } : p));
                      }}
                      onStatusChange={(propId, newStatus) => {
                        // Lost leaves the open list (it lives under the Lost pill) — the server has already saved.
                        if (newStatus === "lost") { setProposals(prev => prev.filter(p => p.id !== propId)); loadData(); return; }
                        setProposals(prev => prev.map(p => p.id === propId ? { ...p, status: newStatus } : p));
                      }}
                      selected={selectedIds.has(proposal.id)}
                      onToggleSelect={toggleSelect}
                    />
                  ))}
                </Fragment>
              ))
            )}
          </TableBody>
        </Table>
        </>)}

        {/* Archived = Won + Lost in one list (toolbar pill). While searching, archived matches also show under the open list. */}
        {view === "open" && search.trim() && archivedItems.length > 0 && (
          <div className="border-t border-slate-200 mt-3" data-testid="archived-search-matches">
            <div className="px-4 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Archived matches · {archivedItems.length}</div>
            <ArchivedProposalsSection
              items={archivedItems}
              getClientName={getClientName}
              onEdit={handleEdit}
              canHardDelete={canHardDelete}
              onHardDelete={async (proposal) => {
                if (!window.confirm(`Permanently delete "${proposal.name}"?\n\nThis cannot be undone. Only use this to remove duplicates.`)) return;
                try {
                  await hardDeleteProposal(proposal.id);
                  toast.success("Duplicate deleted");
                  loadData();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Failed to delete");
                }
              }}
              onRestore={async (proposal) => {
                try {
                  await updateProposal(proposal.id, { ...proposal, status: "draft" });
                  toast.success("Proposal restored to draft");
                  loadData();
                } catch { toast.error("Failed to restore"); }
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
